import React, { memo, useState, useMemo, useEffect, useRef, useCallback, createContext, useContext } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { Box, Typography, CircularProgress, Fade, useMediaQuery, useTheme as useMuiTheme, Tooltip, Chip, IconButton, Menu, MenuItem, Divider, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MarkdownPreview from '@uiw/react-markdown-preview';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ConfidenceLevel from './ConfidenceLevel';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ProblemTreeVisualization from './ProblemTreeVisualization';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import EastIcon from '@mui/icons-material/East';
import WestIcon from '@mui/icons-material/West';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import CircleIcon from '@mui/icons-material/Circle';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import TerminalIcon from '@mui/icons-material/Terminal';
import './AnalysisComponent.css';
import { ThemeContext as AppThemeContext } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useScheduledAnalysis } from '../../hooks/useScheduledAnalysis';

const PrintHeader = ({ viewName, pillarName, subPillarName, analysisDate }) => {
  const printDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const formattedAnalysisDate = analysisDate
    ? new Date(analysisDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'N/A';

  return (
    <div className="print-only-header">
      <h1>{viewName}</h1>
      <h2>{pillarName}{subPillarName ? ` / ${subPillarName}` : ''}</h2>
      <div className="print-meta-data">
        <span><strong>Analysis Date:</strong> {formattedAnalysisDate}</span>
        <span><strong>Printed On:</strong> {printDate}</span>
      </div>
    </div>
  );
};

const escapeHtmlAttribute = (str) => {
  if (!str) return '';
  // Only escape quotes and basic entities, don't double-escape
  return str.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
};

// Function to clean URL from HTML tags
const cleanUrl = (url) => {
  if (!url) return null;
  // Remove any HTML tags from the URL
  return url.replace(/<\/?[^>]+(>|$)/g, '').trim();
};

// Function to extract URL from citation text
const extractUrl = (text) => {
  const urlRegex = /(https?:\/\/[^\s<"]+)/g;
  const matches = text.match(urlRegex);
  if (!matches) return null;
  
  // Clean up the URL by removing any HTML tags
  return cleanUrl(matches[0]);
};

// Throttle function to limit scroll event frequency
const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Wrapper for ConfidenceLevel to support size prop
const ConfidenceLevelWithSize = ({ level, transparent = false, size = 'normal' }) => {
  const theme = useMuiTheme();
  const normalizedLevel = level.toLowerCase();

  // Map confidence levels to numeric scale and descriptions
  const getConfidenceData = () => {
    switch(normalizedLevel) {
      case 'very high':
        return {
          numeric: 5,
          label: 'Very High',
          description: 'The conclusion is strongly supported by a comprehensive, corroborated, and highly credible body of evidence, with excellent methodological rigor and minimal uncertainty.'
        };
      case 'high': 
        return {
          numeric: 4,
          label: 'High',
          description: 'The analysis is well-supported by a robust body of evidence, diverse sources, and sound methodology. Minor limitations do not challenge the conclusion.'
        };
      case 'medium': 
        return {
          numeric: 3,
          label: 'Medium',
          description: 'Confidence is moderate. The evidence base is acceptable but not comprehensive, with some methodological or source limitations.'
        };
      case 'low': 
        return {
          numeric: 2,
          label: 'Low',
          description: 'Confidence is limited due to insufficient evidence, weak methodology, or unresolved inconsistencies. Analytical judgments are tentative.'
        };
      case 'very low':
        return {
          numeric: 1,
          label: 'Very Low',
          description: 'Confidence in the conclusion is very limited. The analysis relies on weak or insufficient evidence, with significant gaps, high uncertainty, and unresolved contradictions.'
        };
      default: 
        return {
          numeric: 0,
          label: normalizedLevel.charAt(0).toUpperCase() + normalizedLevel.slice(1),
          description: 'Confidence level not defined in standard scale.'
        };
    }
  };

  const confidenceData = getConfidenceData();

  const getColor = () => {
    switch(normalizedLevel) {
      case 'very high': return '#1b5e20';  // Darker green
      case 'high': return '#2e7d32';       // Solid green
      case 'medium': return '#ed6c02';     // Solid orange
      case 'low': return '#d32f2f';        // Solid red
      case 'very low': return '#b71c1c';   // Darker red
      default: return theme?.palette?.text?.secondary || '#666';
    }
  };

  const getLEDGlow = () => {
    switch(normalizedLevel) {
      case 'very high': return '0 0 8px rgba(27, 94, 32, 0.6), 0 0 12px rgba(27, 94, 32, 0.3)';
      case 'high': return '0 0 8px rgba(46, 125, 50, 0.6), 0 0 12px rgba(46, 125, 50, 0.3)';
      case 'medium': return '0 0 8px rgba(237, 108, 2, 0.6), 0 0 12px rgba(237, 108, 2, 0.3)';
      case 'low': return '0 0 8px rgba(211, 47, 47, 0.6), 0 0 12px rgba(211, 47, 47, 0.3)';
      case 'very low': return '0 0 8px rgba(183, 28, 28, 0.6), 0 0 12px rgba(183, 28, 28, 0.3)';
      default: return 'none';
    }
  };

  const sizeStyles = size === 'small' ? {
    fontSize: '8px',
    ledSize: '6px',
    gap: 0.5,
    padding: '2px 6px',
    highlightSize: '2px',
  } : {
    fontSize: '9px',
    ledSize: '8px',
    gap: 0.75,
    padding: '4px 8px',
    highlightSize: '3px',
  };
  
  const tooltipContent = (
    <Box sx={{ maxWidth: 450, p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            color: getColor(),
            fontSize: '14px'
          }}
        >
          Confidence Scale
        </Typography>
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        mb: 1.5,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        border: `1px solid ${getColor()}20`
      }}>
        <Box
          sx={{
            width: '12px',
            height: '12px',
            backgroundColor: getColor(),
            borderRadius: '50%',
            flexShrink: 0,
            // boxShadow: getLEDGlow(), // Removed halo effect
            border: `1px solid ${getColor()}`,
          }}
        />
        <Typography 
          variant="body1" 
          sx={{ 
            fontWeight: 600, 
            color: getColor(),
            fontSize: '13px'
          }}
        >
          Level {confidenceData.numeric}/5 - {confidenceData.label}
        </Typography>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 600, mb: 1, color: theme.palette.text.secondary }}>
          Complete Scale:
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {[
            { level: 5, label: 'Very High', desc: 'Comprehensive, corroborated evidence with excellent methodology' },
            { level: 4, label: 'High', desc: 'Well-supported by robust evidence and sound methodology' },
            { level: 3, label: 'Medium', desc: 'Moderate confidence with acceptable but not comprehensive evidence' },
            { level: 2, label: 'Low', desc: 'Limited confidence due to insufficient evidence or weak methodology' },
            { level: 1, label: 'Very Low', desc: 'Very limited confidence with weak/insufficient evidence' }
          ].map((item) => (
            <Box 
              key={item.level}
              sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 1,
                p: 0.5,
                borderRadius: '4px',
                backgroundColor: confidenceData.numeric === item.level ? `${getColor()}15` : 'transparent',
                border: confidenceData.numeric === item.level ? `1px solid ${getColor()}30` : 'none'
            }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '10px', 
                  fontWeight: 600,
                  color: confidenceData.numeric === item.level ? getColor() : theme.palette.text.secondary,
                  minWidth: '12px'
              }}
              >
                {item.level}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '10px', 
                  fontWeight: confidenceData.numeric === item.level ? 600 : 500,
                  color: confidenceData.numeric === item.level ? getColor() : theme.palette.text.secondary,
                  minWidth: '55px'
              }}
              >
                {item.label}:
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '10px',
                  lineHeight: 1.3,
                  color: confidenceData.numeric === item.level ? theme.palette.text.primary : theme.palette.text.secondary,
                  fontWeight: confidenceData.numeric === item.level ? 500 : 400
              }}
              >
                {item.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      
      <Box sx={{ pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: '9px',
            color: theme.palette.text.secondary,
            fontStyle: 'italic'
          }}
        >
          PHASE 1 to 5 Analysis Framework
        </Typography>
      </Box>
    </Box>
  );
  
  return (
    <Tooltip 
      title={tooltipContent}
      arrow
      placement="top"
      enterDelay={200}
      leaveDelay={100}
      PopperProps={{
        sx: {
          '& .MuiTooltip-tooltip': {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            p: 0,
            maxWidth: 'none'
          },
          '& .MuiTooltip-arrow': {
            color: theme.palette.background.paper,
            '&::before': {
              border: `1px solid ${theme.palette.divider}`
          }
          }
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: sizeStyles.gap,
          padding: sizeStyles.padding,
          borderRadius: '12px',
          backgroundColor: transparent ? 'transparent' : theme.palette.action.hover,
          border: transparent ? '1px solid transparent' : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
          minWidth: 'auto',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha(getColor(), 0.08),
            border: `1px solid ${alpha(getColor(), 0.24)}`,
            transform: 'none',
            // boxShadow: `0 2px 8px ${getColor()}20, ${getLEDGlow()}`, // Removed halo effect from hover
            boxShadow: 'none',
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: sizeStyles.fontSize,
            fontWeight: 600,
            color: theme?.palette?.text?.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Confidence
        </Typography>
        <Box
          sx={{
            width: sizeStyles.ledSize,
            height: sizeStyles.ledSize,
            backgroundColor: getColor(),
            borderRadius: '50%',
            flexShrink: 0,
            // boxShadow: getLEDGlow(), // Removed halo effect
            border: `1px solid ${getColor()}`,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '1px',
              left: '1px',
              width: sizeStyles.highlightSize,
              height: sizeStyles.highlightSize,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              borderRadius: '50%',
          },
          }}
        />
      </Box>
    </Tooltip>
  );
};

// Component for impact levels - identical CSS to ConfidenceLevel
const ImpactLevel = ({ level, transparent = false, size = 'normal' }) => {
  const theme = useMuiTheme();
  const normalizedLevel = level.toLowerCase();

  // Map impact levels to numeric scale and descriptions
  const getImpactData = () => {
    switch(normalizedLevel) {
      case 'very high':
      case 'critical':
      case 'severe':
        return {
          numeric: 5,
          label: 'Very High',
          description: '>500k people are affected by the issue'
        };
      case 'high':
        return {
          numeric: 4,
          label: 'High',
          description: '250k–500k people are affected by the issue'
        };
      case 'medium':
      case 'moderate':
        return {
          numeric: 3,
          label: 'Medium',
          description: '100k–250k people are affected by the issue'
        };
      case 'low':
        return {
          numeric: 2,
          label: 'Low',
          description: '50k–100k people are affected by the issue'
        };
      case 'very low':
      case 'minimal':
      case 'negligible':
        return {
          numeric: 1,
          label: 'Very Low',
          description: '10k–50k people are affected by the issue'
        };
      default:
        return {
          numeric: 0,
          label: normalizedLevel.charAt(0).toUpperCase() + normalizedLevel.slice(1),
          description: 'Impact level not defined in standard scale.'
        };
    }
  };

  const impactData = getImpactData();

  // Get mapped level for color consistency
  const getMappedLevel = () => {
    switch(normalizedLevel) {
      case 'very high':
      case 'critical':
      case 'severe':
        return 'very high';
      case 'high':
        return 'high';
      case 'medium':
      case 'moderate':
        return 'medium';
      case 'low':
        return 'low';
      case 'very low':
      case 'minimal':
      case 'negligible':
        return 'very low';
      default:
        return normalizedLevel;
    }
  };

  const mappedLevel = getMappedLevel();

  const getColor = () => {
    switch(mappedLevel) {
      case 'very high': return '#b71c1c';  // Darker red for very high impact (worse)
      case 'high': return '#d32f2f';       // Red for high impact (bad)
      case 'medium': return '#ed6c02';     // Orange for medium impact
      case 'low': return '#2e7d32';        // Green for low impact (good)
      case 'very low': return '#1b5e20';   // Darker green for very low impact (best)
      default: return theme?.palette?.text?.secondary || '#666';
    }
  };

  const getLEDGlow = () => {
    switch(mappedLevel) {
      case 'very high': return '0 0 8px rgba(183, 28, 28, 0.6), 0 0 12px rgba(183, 28, 28, 0.3)';
      case 'high': return '0 0 8px rgba(211, 47, 47, 0.6), 0 0 12px rgba(211, 47, 47, 0.3)';
      case 'medium': return '0 0 8px rgba(237, 108, 2, 0.6), 0 0 12px rgba(237, 108, 2, 0.3)';
      case 'low': return '0 0 8px rgba(46, 125, 50, 0.6), 0 0 12px rgba(46, 125, 50, 0.3)';
      case 'very low': return '0 0 8px rgba(27, 94, 32, 0.6), 0 0 12px rgba(27, 94, 32, 0.3)';
      default: return 'none';
    }
  };

  const sizeStyles = size === 'small' ? {
    fontSize: '8px',
    ledSize: '6px',
    gap: 0.5,
    padding: '2px 6px',
    highlightSize: '2px',
  } : {
    fontSize: '9px',
    ledSize: '8px',
    gap: 0.75,
    padding: '4px 8px',
    highlightSize: '3px',
  };
  
  const tooltipContent = (
    <Box sx={{ maxWidth: 450, p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            color: getColor(),
            fontSize: '14px'
          }}
        >
          Issue Impact Scale
        </Typography>
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        mb: 1.5,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        border: `1px solid ${getColor()}20`
      }}>
        <Box
          sx={{
            width: '12px',
            height: '12px',
            backgroundColor: getColor(),
            borderRadius: '50%',
            flexShrink: 0,
            // boxShadow: getLEDGlow(), // Removed halo effect
            border: `1px solid ${getColor()}`,
          }}
        />
        <Typography 
          variant="body1" 
          sx={{ 
            fontWeight: 600, 
            color: getColor(),
            fontSize: '13px'
          }}
        >
          Level {impactData.numeric}/5 - {impactData.label}
        </Typography>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 600, mb: 1, color: theme.palette.text.secondary }}>
          Complete Scale (Affected Population):
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {[
            { level: 5, label: 'Very High', desc: '>500k people are affected by the issue' },
            { level: 4, label: 'High', desc: '250k–500k people are affected by the issue' },
            { level: 3, label: 'Medium', desc: '100k–250k people are affected by the issue' },
            { level: 2, label: 'Low', desc: '50k–100k people are affected by the issue' },
            { level: 1, label: 'Very Low', desc: '10k–50k people are affected by the issue' }
          ].map((item) => (
            <Box 
              key={item.level}
              sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 1,
                p: 0.5,
                borderRadius: '4px',
                backgroundColor: impactData.numeric === item.level ? `${getColor()}15` : 'transparent',
                border: impactData.numeric === item.level ? `1px solid ${getColor()}30` : 'none'
            }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '10px', 
                  fontWeight: 600,
                  color: impactData.numeric === item.level ? getColor() : theme.palette.text.secondary,
                  minWidth: '12px'
              }}
              >
                {item.level}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '10px', 
                  fontWeight: impactData.numeric === item.level ? 600 : 500,
                  color: impactData.numeric === item.level ? getColor() : theme.palette.text.secondary,
                  minWidth: '55px'
              }}
              >
                {item.label}:
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '10px',
                  lineHeight: 1.3,
                  color: impactData.numeric === item.level ? theme.palette.text.primary : theme.palette.text.secondary,
                  fontWeight: impactData.numeric === item.level ? 500 : 400
              }}
              >
                {item.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      
      <Box sx={{ pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: '9px',
            color: theme.palette.text.secondary,
            fontStyle: 'italic'
          }}
        >
          PHASE 1 Analysis Framework
        </Typography>
      </Box>
    </Box>
  );
  
  return (
    <Tooltip 
      title={tooltipContent}
      arrow
      placement="top"
      enterDelay={200}
      leaveDelay={100}
      PopperProps={{
        sx: {
          '& .MuiTooltip-tooltip': {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            p: 0,
            maxWidth: 'none'
          },
          '& .MuiTooltip-arrow': {
            color: theme.palette.background.paper,
            '&::before': {
              border: `1px solid ${theme.palette.divider}`
          }
          }
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: sizeStyles.gap,
          padding: sizeStyles.padding,
          borderRadius: '12px',
          backgroundColor: transparent ? 'transparent' : theme.palette.action.hover,
          border: transparent ? '1px solid transparent' : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
          minWidth: 'auto',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha(getColor(), 0.08),
            border: `1px solid ${alpha(getColor(), 0.24)}`,
            transform: 'none',
            // boxShadow: `0 2px 8px ${getColor()}20, ${getLEDGlow()}`, // Removed halo effect from hover
            boxShadow: 'none',
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: sizeStyles.fontSize,
            fontWeight: 600,
            color: theme?.palette?.text?.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Impact
        </Typography>
        <Box
          sx={{
            width: sizeStyles.ledSize,
            height: sizeStyles.ledSize,
            backgroundColor: getColor(),
            borderRadius: '50%',
            flexShrink: 0,
            // boxShadow: getLEDGlow(), // Removed halo effect
            border: `1px solid ${getColor()}`,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '1px',
              left: '1px',
              width: sizeStyles.highlightSize,
              height: sizeStyles.highlightSize,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              borderRadius: '50%',
          },
          }}
        />
      </Box>
    </Tooltip>
  );
};

// Generic component for all other scale levels (Contribution, Likelihood, Risk Severity, etc.)
const ScaleLevel = ({ scaleType, level, transparent = false, size = 'normal' }) => {
  const theme = useMuiTheme();
  const normalizedLevel = level.toLowerCase();

  // Get scale-specific data
  const getScaleData = () => {
    switch(scaleType.toLowerCase()) {
      case 'contribution':
        return {
          title: 'Driver Contribution Scale',
          framework: 'PHASE 2 Analysis Framework',
          levels: {
            'very high': { numeric: 5, label: 'Very High', description: 'The main or decisive cause of the observed effect. Effect unlikely without it.' },
            'high': { numeric: 4, label: 'High', description: 'The driver is among the dominant causes. Strong, direct influence.' },
            'medium': { numeric: 3, label: 'Medium', description: 'The driver has a noticeable and measurable influence.' },
            'low': { numeric: 2, label: 'Low', description: 'The driver contributes slightly but is not a key factor.' },
            'very low': { numeric: 1, label: 'Very Low', description: 'The driver plays little to no role in causing the observed effect.' }
          }
        };
      case 'likelihood':
      case 'probability':
        return {
          title: 'Risk Probability Scale',
          framework: 'PHASE 4 Analysis Framework',
          levels: {
            'very likely': { numeric: 5, label: 'Very Likely', description: '>90% probability of occurrence' },
            'likely': { numeric: 4, label: 'Likely', description: '67–90% probability of occurrence' },
            'moderately likely': { numeric: 3, label: 'Moderately Likely', description: '34–66% probability of occurrence' },
            'unlikely': { numeric: 2, label: 'Unlikely', description: '10–33% probability of occurrence' },
            'very unlikely': { numeric: 1, label: 'Very Unlikely', description: '<10% probability of occurrence' }
          }
        };
      case 'risk severity':
      case 'severity':
        return {
          title: 'Risk Severity Scale',
          framework: 'PHASE 4 Analysis Framework',
          levels: {
            'very high': { numeric: 5, label: 'Very High', description: 'Score: 17-25 (Impact × Probability)' },
            'high': { numeric: 4, label: 'High', description: 'Score: 15-16 (Impact × Probability)' },
            'medium': { numeric: 3, label: 'Medium', description: 'Score: 7-14 (Impact × Probability)' },
            'low': { numeric: 2, label: 'Low', description: 'Score: 5-6 (Impact × Probability)' },
            'very low': { numeric: 1, label: 'Very Low', description: 'Score: 1-4 (Impact × Probability)' }
          }
        };
      case 'needs severity':
      case 'needs':
        return {
          title: 'Needs Severity Scale',
          framework: 'PHASE 3 Analysis Framework',
          levels: {
            'critical': { numeric: 5, label: 'Critical', description: 'Life-threatening situation' },
            'severe': { numeric: 4, label: 'Severe', description: 'Major needs unmet, no coping mechanisms' },
            'moderate': { numeric: 3, label: 'Moderate', description: 'Some unmet needs, aid reliance' },
            'of concern': { numeric: 2, label: 'Of Concern', description: 'Early stress indicators' },
            'none': { numeric: 1, label: 'None', description: 'No unmet needs' }
          }
        };
      default:
        return null;
    }
  };

  const scaleData = getScaleData();
  if (!scaleData) return null;

  const levelData = scaleData.levels[normalizedLevel] || scaleData.levels[Object.keys(scaleData.levels).find(key => key.includes(normalizedLevel))];
  if (!levelData) return null;

  // Color scheme - higher values = more concerning (red)
  const getColor = () => {
    switch(levelData.numeric) {
      case 5: return '#b71c1c';  // Very High - dark red
      case 4: return '#d32f2f';  // High - red
      case 3: return '#ed6c02';  // Medium - orange
      case 2: return '#2e7d32';  // Low - green
      case 1: return '#1b5e20';  // Very Low - dark green
      default: return theme?.palette?.text?.secondary || '#666';
    }
  };

  const getLEDGlow = () => {
    const color = getColor();
    const rgba = color.replace('#', '');
    const r = parseInt(rgba.substr(0,2),16);
    const g = parseInt(rgba.substr(2,2),16);
    const b = parseInt(rgba.substr(4,2),16);
    return `0 0 8px rgba(${r}, ${g}, ${b}, 0.6), 0 0 12px rgba(${r}, ${g}, ${b}, 0.3)`;
  };

  const sizeStyles = size === 'small' ? {
    fontSize: '8px',
    ledSize: '6px',
    gap: 0.5,
    padding: '2px 6px',
    highlightSize: '2px',
  } : {
    fontSize: '9px',
    ledSize: '8px',
    gap: 0.75,
    padding: '4px 8px',
    highlightSize: '3px',
  };
  
  const tooltipContent = (
    <Box sx={{ maxWidth: 450, p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            color: getColor(),
            fontSize: '14px'
          }}
        >
          {scaleData.title}
        </Typography>
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        mb: 1.5,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        border: `1px solid ${getColor()}20`
      }}>
        <Box
          sx={{
            width: '12px',
            height: '12px',
            backgroundColor: getColor(),
            borderRadius: '50%',
            flexShrink: 0,
            // boxShadow: getLEDGlow(), // Removed halo effect
            border: `1px solid ${getColor()}`,
          }}
        />
        <Typography 
          variant="body1" 
          sx={{ 
            fontWeight: 600, 
            color: getColor(),
            fontSize: '13px'
          }}
        >
          Level {levelData.numeric}/5 - {levelData.label}
        </Typography>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 600, mb: 1, color: theme.palette.text.secondary }}>
          Complete Scale:
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {Object.entries(scaleData.levels).reverse().map(([key, item]) => (
            <Box 
              key={key}
              sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 1,
                p: 0.5,
                borderRadius: '4px',
                backgroundColor: levelData.numeric === item.numeric ? `${getColor()}15` : 'transparent',
                border: levelData.numeric === item.numeric ? `1px solid ${getColor()}30` : 'none'
            }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '10px', 
                  fontWeight: 600,
                  color: levelData.numeric === item.numeric ? getColor() : theme.palette.text.secondary,
                  minWidth: '12px'
              }}
              >
                {item.numeric}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '10px', 
                  fontWeight: levelData.numeric === item.numeric ? 600 : 500,
                  color: levelData.numeric === item.numeric ? getColor() : theme.palette.text.secondary,
                  minWidth: '75px'
              }}
              >
                {item.label}:
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '10px',
                  lineHeight: 1.3,
                  color: levelData.numeric === item.numeric ? theme.palette.text.primary : theme.palette.text.secondary,
                  fontWeight: levelData.numeric === item.numeric ? 500 : 400
              }}
              >
                {item.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      
      <Box sx={{ pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: '9px',
            color: theme.palette.text.secondary,
            fontStyle: 'italic'
          }}
        >
          {scaleData.framework}
        </Typography>
      </Box>
    </Box>
  );
  
  return (
    <Tooltip 
      title={tooltipContent}
      arrow
      placement="top"
      enterDelay={200}
      leaveDelay={100}
      PopperProps={{
        sx: {
          '& .MuiTooltip-tooltip': {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            p: 0,
            maxWidth: 'none'
          },
          '& .MuiTooltip-arrow': {
            color: theme.palette.background.paper,
            '&::before': {
              border: `1px solid ${theme.palette.divider}`
          }
          }
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: sizeStyles.gap,
          padding: sizeStyles.padding,
          borderRadius: '12px',
          backgroundColor: transparent ? 'transparent' : theme.palette.action.hover,
          border: transparent ? '1px solid transparent' : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
          minWidth: 'auto',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha(getColor(), 0.08),
            border: `1px solid ${alpha(getColor(), 0.24)}`,
            transform: 'none',
            // boxShadow: `0 2px 8px ${getColor()}20, ${getLEDGlow()}`, // Removed halo effect from hover
            boxShadow: 'none',
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: sizeStyles.fontSize,
            fontWeight: 600,
            color: theme?.palette?.text?.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {scaleType}
        </Typography>
        <Box
          sx={{
            width: sizeStyles.ledSize,
            height: sizeStyles.ledSize,
            backgroundColor: getColor(),
            borderRadius: '50%',
            flexShrink: 0,
            // boxShadow: getLEDGlow(), // Removed halo effect
            border: `1px solid ${getColor()}`,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '1px',
              left: '1px',
              width: sizeStyles.highlightSize,
              height: sizeStyles.highlightSize,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              borderRadius: '50%',
          },
          }}
        />
      </Box>
    </Tooltip>
  );
};

// Helper function to render badges in correct order (Impact first, Confidence last, others in between)
const renderOrderedBadges = (levelData, size = 'normal', transparent = false) => {
  const badges = [];
  
  // 1. Impact level always first
  if (levelData.impactLevel) {
    badges.push(
      <ImpactLevel key="impact" level={levelData.impactLevel} transparent={transparent} size={size} />
    );
  }
  
  // 2. Other scale types in the middle
  if (levelData.scaleType && levelData.scaleLevel) {
    badges.push(
      <ScaleLevel key="scale1" scaleType={levelData.scaleType} level={levelData.scaleLevel} transparent={transparent} size={size} />
    );
  }
  if (levelData.scaleType2 && levelData.scaleLevel2) {
    badges.push(
      <ScaleLevel key="scale2" scaleType={levelData.scaleType2} level={levelData.scaleLevel2} transparent={transparent} size={size} />
    );
  }
  if (levelData.scaleType3 && levelData.scaleLevel3) {
    badges.push(
      <ScaleLevel key="scale3" scaleType={levelData.scaleType3} level={levelData.scaleLevel3} transparent={transparent} size={size} />
    );
  }
  
  // 3. Confidence level always last
  if (levelData.confidenceLevel) {
    badges.push(
      <ConfidenceLevelWithSize key="confidence" level={levelData.confidenceLevel} transparent={transparent} size={size} />
    );
  }
  
  return badges;
};

// Function to replace arrow emojis with Material UI icons using theme colors
const replaceArrowEmojis = (content, theme) => {
  if (!content || typeof content !== 'string') return content;
  
  // Define contextual words that indicate positive/negative meaning
  const positiveContextWords = /\b(improving|better|good|positive|beneficial|recovery|stabiliz|progress|gain|increase.*good|decrease.*bad|reduced.*negative|lower.*risk|higher.*quality)\b/i;
  const negativeContextWords = /\b(worsening|worse|bad|negative|harmful|deteriorat|decline|loss|decrease.*good|increase.*bad|reduced.*positive|lower.*quality|higher.*risk)\b/i;
  
  // Function to determine contextual color for arrows
  const getContextualColor = (emoji, surroundingText) => {
    const isUpArrow = /⬆️|↑|↗️|📈/.test(emoji);
    const isDownArrow = /⬇️|↓|↘️|📉/.test(emoji);
    
    // Check for contextual indicators in surrounding text
    const hasPositiveContext = positiveContextWords.test(surroundingText);
    const hasNegativeContext = negativeContextWords.test(surroundingText);
    
    // If there's explicit context, use it to override default arrow meaning
    if (hasPositiveContext) {
      return {
        color: theme.palette.success.main,
        title: isDownArrow ? 'Decreasing (improving)' : 'Increasing (improving)'
      };
    } else if (hasNegativeContext) {
      return {
        color: theme.palette.error.main,
        title: isDownArrow ? 'Decreasing (worsening)' : 'Increasing (worsening)'
      };
    }
    
    // Default behavior - neutral directional indicators
    if (isUpArrow) {
      return {
        color: theme.palette.info.main,
        title: 'Upward direction'
      };
    } else if (isDownArrow) {
      return {
        color: theme.palette.info.main,
        title: 'Downward direction'
      };
    }
    
    // For lateral arrows, return neutral
    return {
      color: theme.palette.info.main,
      title: 'Stable/Lateral movement'
    };
  };
  
  // Define emoji to icon mappings with contextual color detection
  const arrowReplacements = [
    {
      emoji: /⬆️|↑/g,
      icon: 'ArrowUpward',
      getColor: (match, surroundingText) => getContextualColor(match, surroundingText)
    },
    {
      emoji: /⬇️|↓/g,
      icon: 'ArrowDownward', 
      getColor: (match, surroundingText) => getContextualColor(match, surroundingText)
    },
    {
      emoji: /↗️/g,
      icon: 'NorthEast',
      getColor: (match, surroundingText) => getContextualColor(match, surroundingText)
    },
    {
      emoji: /↘️/g,
      icon: 'SouthEast',
      getColor: (match, surroundingText) => getContextualColor(match, surroundingText)
    },
    {
      emoji: /➡️|→/g,
      icon: 'East',
      color: theme.palette.info.main,
      title: 'Stable/Lateral movement'
    },
    {
      emoji: /⬅️|←/g,
      icon: 'West',
      color: theme.palette.info.main,
      title: 'Reverse/Backward movement'
    },
    {
      emoji: /📈/g,
      icon: 'TrendingUp',
      getColor: (match, surroundingText) => getContextualColor(match, surroundingText)
    },
    {
      emoji: /📉/g,
      icon: 'TrendingDown',
      getColor: (match, surroundingText) => getContextualColor(match, surroundingText)
    },
    // Colored circle emojis (unchanged)
    {
      emoji: /🟢/g,
      icon: 'Circle',
      color: theme.palette.success.main,
      title: 'High/Good/Positive'
    },
    {
      emoji: /🟡/g,
      icon: 'Circle',
      color: theme.palette.warning.main,
      title: 'Medium/Moderate'
    },
    {
      emoji: /🔴/g,
      icon: 'Circle',
      color: theme.palette.error.main,
      title: 'Low/Poor/Negative'
    },
    {
      emoji: /🟠/g,
      icon: 'Circle',
      color: '#ff9800', // Orange
      title: 'Medium-High'
    },
    {
      emoji: /🔵/g,
      icon: 'Circle',
      color: theme.palette.info.main,
      title: 'Information/Neutral'
    },
    {
      emoji: /🟣/g,
      icon: 'Circle',
      color: 'var(--color-secondary)',
      title: 'Special/Other'
    }
  ];

  let processedContent = content;
  
  arrowReplacements.forEach(({ emoji, icon, color, title, getColor }) => {
    // Find all matches with their positions for context analysis
    const matches = [...processedContent.matchAll(emoji)];
    
    // Process matches in reverse order to avoid position shifts
    for (let i = matches.length - 1; i >= 0; i--) {
      const match = matches[i];
      const matchStart = match.index;
      const matchEnd = matchStart + match[0].length;
      
      // Get surrounding text for context (50 characters before and after)
      const surroundingStart = Math.max(0, matchStart - 50);
      const surroundingEnd = Math.min(processedContent.length, matchEnd + 50);
      const surroundingText = processedContent.slice(surroundingStart, surroundingEnd);
      
      // Determine color and title
      let finalColor = color;
      let finalTitle = title;
      
      if (getColor) {
        const contextualResult = getColor(match[0], surroundingText);
        finalColor = contextualResult.color;
        finalTitle = contextualResult.title;
      }
      
      const replacement = `<span class="emoji-icon-replacement" data-icon="${icon}" data-color="${finalColor}" data-title="${finalTitle}">🔄</span>`;
      
      processedContent = processedContent.substring(0, matchStart) + replacement + processedContent.substring(matchEnd);
    }
  });
  
  return processedContent;
};

// Custom span component to render Material UI icons for emoji replacements
const CustomSpan = ({ node, className, children, ...props }) => {
  const theme = useMuiTheme();
  
  // Handle emoji icon replacements
  if (className === 'emoji-icon-replacement') {
    const iconName = props['data-icon'];
    const color = props['data-color'];
    const title = props['data-title'];
    
    // Map icon names to actual Material UI icon components
    const IconComponent = {
      'ArrowUpward': ArrowUpwardIcon,
      'ArrowDownward': ArrowDownwardIcon,
      'NorthEast': NorthEastIcon,
      'SouthEast': SouthEastIcon,
      'East': EastIcon,
      'West': WestIcon,
      'TrendingUp': TrendingUpIcon,
      'TrendingDown': TrendingDownIcon,
      'Circle': CircleIcon,
    }[iconName];
    
    if (IconComponent) {
      // Special styling for circle icons to match confidence level indicators
      if (iconName === 'Circle') {
        // Calculate glow based on color
        const getLEDGlow = (color) => {
          // Extract RGB values from color
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = color;
          const computedColor = ctx.fillStyle;
          
          // Convert to rgba for glow
          if (computedColor.startsWith('#')) {
            const r = parseInt(computedColor.slice(1, 3), 16);
            const g = parseInt(computedColor.slice(3, 5), 16);
            const b = parseInt(computedColor.slice(5, 7), 16);
            return `0 0 6px rgba(${r}, ${g}, ${b}, 0.6), 0 0 10px rgba(${r}, ${g}, ${b}, 0.3)`;
          }
          return `0 0 6px ${color}60, 0 0 10px ${color}30`;
        };

        return (
          <Tooltip title={title} arrow placement="top">
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                verticalAlign: 'middle',
                mx: 0.25,
                position: 'relative',
            }}
            >
              <Box
                sx={{
                  width: '10px',
                  height: '10px',
                  backgroundColor: color,
                  borderRadius: '50%',
                  border: `1px solid ${color}`,
                  // boxShadow: getLEDGlow(color), // Removed halo effect
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '1px',
                    left: '1px',
                    width: '2px',
                    height: '2px',
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: '50%',
                },
              }}
              />
            </Box>
          </Tooltip>
        );
      }
      
      // Regular arrow and trend icons
      return (
        <Tooltip title={title} arrow placement="top">
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              verticalAlign: 'middle',
              color: color,
              fontSize: '1.1em',
              mx: 0.25,
          }}
          >
            <IconComponent sx={{ fontSize: 'inherit' }} />
          </Box>
        </Tooltip>
      );
    }
  }
  
  // Handle citation spans (move existing logic here)
  if (className === 'citation') {
    const tooltipContent = props['data-tooltip'];
    return (
      <Tooltip 
        title={
          <div 
            style={{ 
              whiteSpace: 'pre-line',
              fontSize: 'var(--font-size-md)',
              lineHeight: 'var(--line-height-normal)',
              maxWidth: '500px',
              overflowWrap: 'break-word'
          }}
            dangerouslySetInnerHTML={{ __html: tooltipContent }}
          />
        }
        arrow
        placement="top-start"
        enterDelay={100}
        leaveDelay={50}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 100 }}
        PopperProps={{
          sx: {
            '& .MuiTooltip-tooltip': {
              maxWidth: 500,
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              p: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              borderRadius: '8px'
          },
            '& .MuiTooltip-arrow': {
              color: 'background.paper',
              '&::before': {
                border: '1px solid',
                borderColor: 'divider'
            }
          }
          }
        }}
        followCursor={true}
        disableInteractive={false}
      >
        <span 
          className={className}
          style={{ 
            cursor: 'pointer', 
            color: 'var(--color-primary)',
          }}
          onClick={(e) => {
            e.preventDefault();
            const url = extractUrl(tooltipContent);
            if (url) {
              window.open(url, '_blank', 'noopener,noreferrer');
          }
          }}
        >
          {children}
        </span>
      </Tooltip>
    );
  }
  
  // Default span rendering
  return <span className={className} {...props}>{children}</span>;
};

// Custom paragraph component to handle confidence levels
const CustomParagraph = ({ children, ...props }) => {
  // Check if this paragraph contains a confidence level
  const paragraphRef = useRef(null);
  const [isConfidenceLevel, setIsConfidenceLevel] = useState(false);
  const [confidenceLevel, setConfidenceLevel] = useState(null);
  const [isRawHtmlConfidence, setIsRawHtmlConfidence] = useState(false);
  const [hasLevelBadges, setHasLevelBadges] = useState(false);
  const [levelData, setLevelData] = useState(null);
  const theme = useMuiTheme();
  
  useEffect(() => {
    if (paragraphRef.current) {
      const element = paragraphRef.current;
      const html = element.innerHTML;
      const textContent = element.textContent || '';

      // First check for multi-line level patterns like "Impact level - Medium\nConfidence level - High"
      const multiLineLevelPattern = /^(Impact|Confidence|Contribution|Likelihood|Risk severity|Severity|Needs severity|Needs)\s+(level|score)\s*[-–:]\s*((?:\d+\s*[-–]\s*)?(?:Very High|High|Medium|Low|Very Low|Very Likely|Likely|Moderately Likely|Unlikely|Very Unlikely|Critical|Severe|Moderate|Of Concern|None))\s*[\r\n]+\s*(Impact|Confidence|Contribution|Likelihood|Risk severity|Severity|Needs severity|Needs)\s+(level|score)\s*[-–:]\s*((?:\d+\s*[-–]\s*)?(?:Very High|High|Medium|Low|Very Low|Very Likely|Likely|Moderately Likely|Unlikely|Very Unlikely|Critical|Severe|Moderate|Of Concern|None))\s*$/i;
      const multiLineMatch = textContent.match(multiLineLevelPattern);
      
      if (multiLineMatch) {
        const firstType = multiLineMatch[1]?.toLowerCase();
        const firstValue = multiLineMatch[3]?.trim()?.replace(/^\d+\s*[-–]\s*/, ''); // Add safety checks
        const secondType = multiLineMatch[4]?.toLowerCase();
        const secondValue = multiLineMatch[6]?.trim()?.replace(/^\d+\s*[-–]\s*/, ''); // Add safety checks
        
        // Only proceed if we have all required values
        if (firstType && firstValue && secondType && secondValue) {
          const extractedLevels = {};
          
          // Process first level
          if (firstType === 'impact') {
            extractedLevels.impactLevel = firstValue;
          } else if (firstType === 'confidence') {
            extractedLevels.confidenceLevel = firstValue.toLowerCase();
          } else {
            extractedLevels.scaleType = firstType;
            extractedLevels.scaleLevel = firstValue;
          }
          
          // Process second level
          if (secondType === 'impact') {
            extractedLevels.impactLevel = secondValue;
          } else if (secondType === 'confidence') {
            extractedLevels.confidenceLevel = secondValue.toLowerCase();
          } else {
            extractedLevels.scaleType2 = secondType;
            extractedLevels.scaleLevel2 = secondValue;
          }
          
          if (Object.keys(extractedLevels).length > 0) {
            setHasLevelBadges(true);
            setLevelData(extractedLevels);
            return;
          }
        } else {
          console.warn('Missing required groups in multi-line match:', { firstType, firstValue, secondType, secondValue, groups: multiLineMatch });
        }
      }

      // Check for mixed scale patterns (score: and level -)
      const mixedScalePattern = /(Likelihood|Probability)\s+(score|level)\s*:\s*(Very High|High|Medium|Low|Very Low|Very Likely|Likely|Moderately Likely|Unlikely|Very Unlikely)\s+(Impact|Confidence|Contribution|Risk severity|Severity|Needs severity|Needs)\s+(level|score)\s*[-–]\s*(Very High|High|Medium|Low|Very Low|Critical|Severe|Moderate|Of Concern|None)\s+(Risk severity|Severity|Needs severity|Needs|Contribution|Impact|Confidence)\s+(level|score)\s*[-–]\s*(Very High|High|Medium|Low|Very Low|Critical|Severe|Moderate|Of Concern|None)/i;
      const mixedMatch = textContent.match(mixedScalePattern);
      
      if (mixedMatch) {
        const extractedLevels = {};
        
        // First scale (with colon)
        const firstType = mixedMatch[1]?.toLowerCase();
        const firstValue = mixedMatch[3]?.trim();
        
        // Second scale (with hyphen) 
        const secondType = mixedMatch[4]?.toLowerCase();
        const secondValue = mixedMatch[6]?.trim();
        
        // Third scale (with hyphen)
        const thirdType = mixedMatch[7]?.toLowerCase();
        const thirdValue = mixedMatch[9]?.trim();
        
        // Only proceed if we have the basic required values
        if (firstType && firstValue && secondType && secondValue && thirdType && thirdValue) {
          // Process each scale
          if (firstType === 'impact') {
            extractedLevels.impactLevel = firstValue;
          } else if (firstType === 'confidence') {
            extractedLevels.confidenceLevel = firstValue.toLowerCase();
          } else {
            extractedLevels.scaleType = firstType;
            extractedLevels.scaleLevel = firstValue;
          }
          
          if (secondType === 'impact') {
            extractedLevels.impactLevel = secondValue;
          } else if (secondType === 'confidence') {
            extractedLevels.confidenceLevel = secondValue.toLowerCase();
          } else {
            extractedLevels.scaleType2 = secondType;
            extractedLevels.scaleLevel2 = secondValue;
          }
          
          if (thirdType === 'impact') {
            extractedLevels.impactLevel = thirdValue;
          } else if (thirdType === 'confidence') {
            extractedLevels.confidenceLevel = thirdValue.toLowerCase();
          } else {
            extractedLevels.scaleType3 = thirdType;
            extractedLevels.scaleLevel3 = thirdValue;
          }
          
          if (Object.keys(extractedLevels).length > 0) {
            setHasLevelBadges(true);
            setLevelData(extractedLevels);
            return;
          }
        } else {
          console.warn('Missing required groups in mixed scale match:', { firstType, firstValue, secondType, secondValue, thirdType, thirdValue, groups: mixedMatch });
        }
      }

      // Check for HTML strong tag patterns like "<strong>Impact Level: 5 – Very High</strong>"
      const htmlStrongLevelPattern = /<strong[^>]*>\s*(Impact|Confidence|Contribution|Likelihood|Risk severity|Severity|Needs severity|Needs)\s+(level|score)\s*[:–-]\s*(?:\d+\s*[-–]\s*)?(Very High|High|Medium|Low|Very Low|Very Likely|Likely|Moderately Likely|Unlikely|Very Unlikely|Critical|Severe|Moderate|Of Concern|None)\s*<\/strong>/gi;
      const htmlLevelMatches = [...html.matchAll(htmlStrongLevelPattern)];
      
      if (htmlLevelMatches.length > 0) {
        const extractedLevels = {};
        htmlLevelMatches.forEach(match => {
          const levelType = match[1]?.toLowerCase();
          const levelValue = match[3]?.trim(); // Add safety check
          
          // Only proceed if we have both type and value
          if (levelType && levelValue) {
            if (levelType === 'impact') {
              extractedLevels.impactLevel = levelValue;
          } else if (levelType === 'confidence') {
              extractedLevels.confidenceLevel = levelValue.toLowerCase();
          } else {
              // For multiple other scales, use numbered properties
              if (!extractedLevels.scaleType) {
                extractedLevels.scaleType = levelType;
                extractedLevels.scaleLevel = levelValue;
            } else if (!extractedLevels.scaleType2) {
                extractedLevels.scaleType2 = levelType;
                extractedLevels.scaleLevel2 = levelValue;
            } else if (!extractedLevels.scaleType3) {
                extractedLevels.scaleType3 = levelType;
                extractedLevels.scaleLevel3 = levelValue;
            }
          }
          } else {
            console.warn('Missing required groups in HTML strong match:', { levelType, levelValue, groups: match });
          }
        });
        
        if (Object.keys(extractedLevels).length > 0) {
          setHasLevelBadges(true);
          setLevelData(extractedLevels);
          return; // Skip other confidence detection since we found level badges
        }
      }

      // Check for bold level patterns like "**Impact level - High**" and "**Confidence level - Medium**"
      const boldLevelPattern = /\*\*\s*(Impact|Confidence|Contribution|Likelihood|Risk severity|Severity|Needs severity|Needs)\s+(level|score)\s*[-–:]\s*(?:\d+\s*[-–]\s*)?(Very High|High|Medium|Low|Very Low|Very Likely|Likely|Moderately Likely|Unlikely|Very Unlikely|Critical|Severe|Moderate|Of Concern|None)\s*\*\*/gi;
      const levelMatches = [...textContent.matchAll(boldLevelPattern)];
      
      if (levelMatches.length > 0) {
        const extractedLevels = {};
        levelMatches.forEach(match => {
          const levelType = match[1]?.toLowerCase();
          const levelValue = match[4]?.trim(); // Add safety check
          
          // Only proceed if we have both type and value
          if (levelType && levelValue) {
            if (levelType === 'impact') {
              extractedLevels.impactLevel = levelValue;
          } else if (levelType === 'confidence') {
              extractedLevels.confidenceLevel = levelValue.toLowerCase();
          } else {
              // For multiple other scales, use numbered properties
              if (!extractedLevels.scaleType) {
                extractedLevels.scaleType = levelType;
                extractedLevels.scaleLevel = levelValue;
            } else if (!extractedLevels.scaleType2) {
                extractedLevels.scaleType2 = levelType;
                extractedLevels.scaleLevel2 = levelValue;
            } else if (!extractedLevels.scaleType3) {
                extractedLevels.scaleType3 = levelType;
                extractedLevels.scaleLevel3 = levelValue;
            }
          }
          } else {
            console.warn('Missing required groups in bold level match:', { levelType, levelValue, groups: match });
          }
        });
        
        if (Object.keys(extractedLevels).length > 0) {
          setHasLevelBadges(true);
          setLevelData(extractedLevels);
          return; // Skip other confidence detection since we found level badges
        }
      }

      // Check for raw HTML structure: <div class="confidence-level-container LEVEL ...">...</div>
      // This regex is a bit simplified, assuming the first child div has the class.
      const directChildDiv = element.querySelector(':scope > div.confidence-level-container');
      if (directChildDiv) {
        const classList = directChildDiv.className;
        const levelMatch = classList.match(/\b(high|medium|low)\b/i);
        if (levelMatch && levelMatch[1]) {
          setIsConfidenceLevel(true);
          setConfidenceLevel(levelMatch[1].toLowerCase());
          setIsRawHtmlConfidence(true); // Mark that this was found via raw HTML
          return; // Prioritize raw HTML match
        }
      }

      // If not raw HTML, check for textual patterns
      // Pattern 1: "confidence level : (high|medium|low)" with optional HTML tags around colon and level
      const colonSyntaxRegex = /confidence level\s*(?:<\/[^>]+>\s*)?:\s*(?:<[^>]+>\s*)?(high|medium|low)\s*(?:<\/[^>]+>)?/i;
      // Pattern 2: "<strong>Confidence level - (high|medium|low)</strong>" or <b> equivalent
      const boldHyphenSyntaxRegex = /(?:<strong>|<b>)\s*Confidence level\s*[-–]\s*(high|medium|low)\s*(?:<\/strong>|<\/b>)/i;

      let match = html.match(colonSyntaxRegex);
      if (match && match[1]) {
        setIsConfidenceLevel(true);
        setConfidenceLevel(match[1].toLowerCase());
      } else {
        match = html.match(boldHyphenSyntaxRegex);
        if (match && match[1]) {
          setIsConfidenceLevel(true);
          setConfidenceLevel(match[1].toLowerCase());
        }
      }
    }
  }, [children]);
  
  // If this paragraph has level badges, render them
  if (hasLevelBadges && levelData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
        {renderOrderedBadges(levelData)}
      </div>
    );
  }
  
  // If this is a confidence level paragraph, render our custom component
  if (isConfidenceLevel && confidenceLevel) {
    // If it was a raw HTML confidence, we want to render it inline
    // The ConfidenceLevel component itself does not have absolute positioning by default
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <ConfidenceLevelWithSize level={confidenceLevel} />
      </div>
    );
  }
  
  // If not a confidence level, render as normal paragraph
  return <p {...props} ref={paragraphRef}>{children}</p>;
};

// Custom h3 component to handle confidence levels in h3 tags
const CustomH3 = ({ children, isInsideSourcesSection = false, node, ...props }) => {
  const h3Ref = useRef(null);
  const [isConfidenceLevel, setIsConfidenceLevel] = useState(false);
  const [confidenceLevel, setConfidenceLevel] = useState(null);
  const [isRawHtmlConfidence, setIsRawHtmlConfidence] = useState(false);
  const [hasParentheticalLevels, setHasParentheticalLevels] = useState(false);
  const [parentheticalData, setParentheticalData] = useState(null);
  const [cleanedTitle, setCleanedTitle] = useState(children);
  const [isStandaloneLevelHeader, setIsStandaloneLevelHeader] = useState(false);
  const [standaloneLevelData, setStandaloneLevelData] = useState(null);
  const [shouldSkipRender, setShouldSkipRender] = useState(false);
  const [combinedLevelData, setCombinedLevelData] = useState(null);
  const theme = useMuiTheme();
  const { currentTheme: appTheme } = useContext(AppThemeContext);
  
  useEffect(() => {
    if (h3Ref.current) {
      const element = h3Ref.current;
      const htmlContent = element.innerHTML;
      const textContent = element.textContent || '';
      const idAttr = element.id || '';

      // Check if this element was already marked to be skipped
      if (element.dataset.levelSkip === 'true') {
        setShouldSkipRender(true);
        return;
      }

      // First check for standalone level headers like "Impact level - High" or "Confidence level - Medium"
      const standaloneLevelPattern = /^(Impact|Confidence|Contribution|Likelihood|Risk severity|Severity|Needs severity|Needs)\s+(level|score)\s*[:\-–]\s*(?:\d+\s*[-–]\s*)?(Very High|High|Medium|Low|Very Low|Very Likely|Likely|Moderately Likely|Unlikely|Very Unlikely|Critical|Severe|Moderate|Of Concern|None)$/i;
      const standaloneLevelMatch = textContent.match(standaloneLevelPattern);
      
      if (standaloneLevelMatch) {
        const levelType = standaloneLevelMatch[1]?.toLowerCase();
        const levelValue = standaloneLevelMatch[3]?.trim(); // Fixed: Use group 3, not 4
        
        // Only proceed if we have both type and value
        if (levelType && levelValue) {
          const extractedData = {};
          // Check if the next sibling is another level header that should be combined
          if (levelType === 'impact') {
            extractedData.impactLevel = levelValue;
          } else if (levelType === 'confidence') {
            extractedData.confidenceLevel = levelValue.toLowerCase();
          } else {
            extractedData.scaleType = levelType;
            extractedData.scaleLevel = levelValue;
          }
          
          
          // Check if the next sibling is another level header that should be combined
          if (levelType === 'impact') {
          const nextSibling = element.nextElementSibling;
            if (nextSibling && nextSibling.tagName === 'H3') {
              const nextText = nextSibling.textContent || '';
              const nextLevelMatch = nextText.match(standaloneLevelPattern);
              if (nextLevelMatch) {
                const nextLevelType = nextLevelMatch[1]?.toLowerCase();
                const nextLevelValue = nextLevelMatch[3]?.trim(); // Fixed: Use group 3, not 4
                
                if (nextLevelType && nextLevelValue) {
                  // Mark the next element to be skipped
                  nextSibling.dataset.levelSkip = 'true';
                  
                  // Add the next scale type to our extracted data
                  if (nextLevelType === 'confidence') {
                    extractedData.confidenceLevel = nextLevelValue.toLowerCase();
                } else {
                    if (!extractedData.scaleType2) {
                    extractedData.scaleType2 = nextLevelType;
                    extractedData.scaleLevel2 = nextLevelValue;
                } else {
                    extractedData.scaleType3 = nextLevelType;
                    extractedData.scaleLevel3 = nextLevelValue;
                }
                }
                  
                  setCombinedLevelData(extractedData);
                  setIsStandaloneLevelHeader(true);
                  return;
              }
            }
          }
          }
          
          if (Object.keys(extractedData).length > 0) {
            setIsStandaloneLevelHeader(true);
            setStandaloneLevelData(extractedData);
            return;
          }
        } else {
          console.warn('Missing required groups in standalone level match:', { levelType, levelValue, groups: standaloneLevelMatch });
        }
      }

      // Check for parenthetical levels pattern like "(Impact Level: Very High, Confidence Level: High)"
      const parentheticalPattern = /^(.*?)\s*\(\s*(.+?)\s*\)\s*$/i;
      const parentheticalMatch = textContent.match(parentheticalPattern);
      
      if (parentheticalMatch) {
        const titlePart = parentheticalMatch[1].trim();
        const levelsPart = parentheticalMatch[2];
        
        // Check if the parentheses contain impact or confidence level information
        const hasImpactOrConfidence = /(?:Impact Level|Confidence Level|Contribution Level|Likelihood|Risk severity|Severity|Needs severity|Needs):/i.test(levelsPart);
        
        if (hasImpactOrConfidence) {
          // Extract individual levels
          const impactMatch = levelsPart.match(/Impact Level:\s*([^,]+)/i);
          const confidenceMatch = levelsPart.match(/Confidence Level:\s*([^,)]+)/i);
          const contributionMatch = levelsPart.match(/Contribution Level:\s*([^,)]+)/i);
          const likelihoodMatch = levelsPart.match(/Likelihood:\s*([^,)]+)/i);
          const riskSeverityMatch = levelsPart.match(/Risk severity:\s*([^,)]+)/i);
          const severityMatch = levelsPart.match(/Severity:\s*([^,)]+)/i);
          const needsSeverityMatch = levelsPart.match(/Needs severity:\s*([^,)]+)/i);
          const needsMatch = levelsPart.match(/Needs:\s*([^,)]+)/i);
          
          const extractedData = {};
          if (impactMatch) {
            extractedData.impactLevel = impactMatch[1].trim();
          }
          if (confidenceMatch) {
            extractedData.confidenceLevel = confidenceMatch[1].trim().toLowerCase();
          }
          if (contributionMatch) {
            extractedData.scaleType = 'contribution';
            extractedData.scaleLevel = contributionMatch[1].trim();
          }
          if (likelihoodMatch) {
            extractedData.scaleType = 'likelihood';
            extractedData.scaleLevel = likelihoodMatch[1].trim();
          }
          if (riskSeverityMatch) {
            extractedData.scaleType = 'risk severity';
            extractedData.scaleLevel = riskSeverityMatch[1].trim();
          }
          if (severityMatch) {
            extractedData.scaleType = 'severity';
            extractedData.scaleLevel = severityMatch[1].trim();
          }
          if (needsSeverityMatch) {
            extractedData.scaleType = 'needs severity';
            extractedData.scaleLevel = needsSeverityMatch[1].trim();
          }
          if (needsMatch) {
            extractedData.scaleType = 'needs';
            extractedData.scaleLevel = needsMatch[1].trim();
          }
          
          if (Object.keys(extractedData).length > 0) {
            setHasParentheticalLevels(true);
            setParentheticalData(extractedData);
            setCleanedTitle(titlePart);
            return;
          }
        }
      }

      // Check for raw HTML structure within H3
      const directChildDiv = element.querySelector(':scope > div.confidence-level-container');
      if (directChildDiv) {
        const classList = directChildDiv.className;
        const levelMatch = classList.match(/\b(high|medium|low)\b/i);
        if (levelMatch && levelMatch[1]) {
          setIsConfidenceLevel(true);
          setConfidenceLevel(levelMatch[1].toLowerCase());
          setIsRawHtmlConfidence(true);
          return; 
        }
      }

      // Pattern 1: Check id attribute like id="confidence-level-low" or "confidence-level--low"
      const idRegex = /^confidence-level(?:--|-)?(high|medium|low)$/i;
      const idMatch = idAttr.match(idRegex);

      if (idMatch && idMatch[1]) {
        setIsConfidenceLevel(true);
        setConfidenceLevel(idMatch[1].toLowerCase());
      } else {
        // If ID doesn't match, check content of the H3 for textual patterns
        const colonSyntaxContentRegex = /confidence level\s*(?:<\/[^>]+>\s*)?:\s*(high|medium|low)/i;
        const boldHyphenSyntaxContentRegex = /(?:<strong>|<b>)\s*Confidence level\s*[-–]\s*(high|medium|low)\s*(?:<\/strong>|<\/b>)/i;

        let contentMatch = htmlContent.match(colonSyntaxContentRegex);
        if (contentMatch && contentMatch[1]) {
          setIsConfidenceLevel(true);
          setConfidenceLevel(contentMatch[1].toLowerCase());
        } else {
          contentMatch = htmlContent.match(boldHyphenSyntaxContentRegex);
          if (contentMatch && contentMatch[1]) {
            setIsConfidenceLevel(true);
            setConfidenceLevel(contentMatch[1].toLowerCase());
          }
        }
      }
    }
  }, [children, node]);
  
  // If this element should be skipped (already rendered by previous impact level)
  if (shouldSkipRender) {
    return null;
  }
  
  // If this is a combined level header (impact + confidence), render both badges together
  if (combinedLevelData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
        {renderOrderedBadges(combinedLevelData, 'normal')}
      </div>
    );
  }
  
  // If this is a standalone level header, render as badges
  if (isStandaloneLevelHeader && standaloneLevelData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
        {renderOrderedBadges(standaloneLevelData, 'normal')}
      </div>
    );
  }
  
  if (hasParentheticalLevels && parentheticalData) {
    const parentNode = node?.parent;
    const grandparentNode = parentNode?.parent;
    const isInsideBlockquote = parentNode?.type === 'blockquote' || grandparentNode?.type === 'blockquote';
    let parentDivHasInlineBackground = false;

    const checkDivStyle = (divNode) => {
      if (divNode?.type === 'element' && divNode?.tagName?.toLowerCase() === 'div') {
        const styleProperty = divNode.properties?.style;
        if (typeof styleProperty === 'string' && styleProperty.length > 0) {
          const styleString = styleProperty.toLowerCase();
          if (process.env.NODE_ENV === 'development') {
            // console.log(`Div check for H3: node tagName=${divNode.tagName}, styleString='${styleString}'`);
          }
          if (styleString.includes('background-color') || styleString.includes('background:')) {
            return true;
          }
        }
      }
      return false;
    };

    if (checkDivStyle(parentNode)) {
      parentDivHasInlineBackground = true;
    } else if (checkDivStyle(grandparentNode)) {
      // Only consider grandparent if direct parent wasn't a styled div AND direct parent is simple (e.g. P tag)
      // This avoids incorrectly styling if H3 is in Div > Div (no bg) > Div (with bg)
      if (parentNode?.type === 'element' && (parentNode?.tagName?.toLowerCase() === 'p' || parentNode?.tagName?.toLowerCase() === 'span')) {
         parentDivHasInlineBackground = true;
      }
    }
    const omitStyles = isInsideSourcesSection || isInsideBlockquote || parentDivHasInlineBackground;

    const style = {
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      width: '100%', gap: '12px',
    };
    if (omitStyles) {
      style.marginTop = '0px'; style.paddingTop = '0px'; style.marginBottom = '7px';
    } else {
      style.marginTop = '9px'; style.paddingTop = '14px';
      style.marginBottom = '14px'; style.borderTop = '2px solid #ececec';
    }
    // Determine if title has prefix (Scenario/Strategic Objective/Topic/Risk)
    const prefixMatch = cleanedTitle.match(/^(Scenario \d+|Strategic Objective \d+|Topic \d+|Risk \d+):\s*(.+)$/i);
    let prefixText = null;
    let displayTitle = cleanedTitle;
    let isRiskPrefix = false;
    if (prefixMatch) {
      prefixText = prefixMatch[1];
      isRiskPrefix = /^Risk \d+/i.test(prefixText);
      displayTitle = prefixMatch[2];
    }
    return (
      <div style={style}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: '0', flex: '1', minWidth: '0' }} {...props} ref={h3Ref}>
            {prefixText && <span style={{ color: isRiskPrefix ? 'var(--color-error)' : 'var(--color-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>{prefixText}: </span>}
            {displayTitle}
          </h3>
        </Box>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center' }}>
          {renderOrderedBadges(parentheticalData, 'normal')}
        </div>
      </div>
    );
  }
  
  if (isConfidenceLevel && confidenceLevel) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <ConfidenceLevelWithSize level={confidenceLevel} />
      </div>
    );
  }
  
  return <h3 {...props} ref={h3Ref}>{children}</h3>;
};

// Custom h4 component to handle confidence levels in h4 tags (similar to CustomH3)
const CustomH4 = ({ children, isInsideSourcesSection = false, node, ...props }) => {
  const h4Ref = useRef(null);
  const [isConfidenceLevel, setIsConfidenceLevel] = useState(false);
  const [confidenceLevel, setConfidenceLevel] = useState(null);
  const [isRawHtmlConfidence, setIsRawHtmlConfidence] = useState(false);
  const [hasParentheticalLevels, setHasParentheticalLevels] = useState(false);
  const [parentheticalData, setParentheticalData] = useState(null);
  const [cleanedTitle, setCleanedTitle] = useState(children);
  const [isStandaloneLevelHeader, setIsStandaloneLevelHeader] = useState(false);
  const [standaloneLevelData, setStandaloneLevelData] = useState(null);
  const [shouldSkipRender, setShouldSkipRender] = useState(false);
  const [combinedLevelData, setCombinedLevelData] = useState(null);
  const theme = useMuiTheme();
  
  useEffect(() => {
    if (h4Ref.current) {
      const element = h4Ref.current;
      const htmlContent = element.innerHTML;
      const textContent = element.textContent || '';
      const idAttr = element.id || '';

      // Check if this element was already marked to be skipped
      if (element.dataset.levelSkip === 'true') {
        setShouldSkipRender(true);
        return;
      }

      // First check for standalone level headers like "Impact Level: High" or "Confidence Level: Medium"
      const standaloneLevelPattern = /^(Impact|Confidence|Contribution|Likelihood|Risk severity|Severity|Needs severity|Needs)\s+(level|score)\s*[:\-–]\s*(?:\d+\s*[-–]\s*)?(Very High|High|Medium|Low|Very Low|Very Likely|Likely|Moderately Likely|Unlikely|Very Unlikely|Critical|Severe|Moderate|Of Concern|None)$/i;
      
      const standaloneLevelMatch = textContent.match(standaloneLevelPattern);
      
      if (standaloneLevelMatch) {
        const levelType = standaloneLevelMatch[1]?.toLowerCase();
        const levelValue = standaloneLevelMatch[3]?.trim(); // Changed from [4] to [3]
        
        if (levelType && levelValue) {
          const extractedData = {};
          // Check if the next sibling is another level header that should be combined
          if (levelType === 'impact') {
            extractedData.impactLevel = levelValue;
          } else if (levelType === 'confidence') {
            extractedData.confidenceLevel = levelValue.toLowerCase();
          } else {
            extractedData.scaleType = levelType;
            extractedData.scaleLevel = levelValue;
          }
          
          
          // Check if the next sibling is another level header that should be combined
          if (levelType === 'impact') {
          const nextSibling = element.nextElementSibling;
            if (nextSibling && (nextSibling.tagName === 'H5' || nextSibling.tagName === 'H4' || nextSibling.tagName === 'H3')) {
              const nextText = nextSibling.textContent || '';
              const nextLevelMatch = nextText.match(standaloneLevelPattern);
              if (nextLevelMatch) {
                const nextLevelType = nextLevelMatch[1]?.toLowerCase();
                const nextLevelValue = nextLevelMatch[3]?.trim(); // Fixed from [4] to [3]
                
                if (nextLevelType && nextLevelValue) {
                  // Mark the next element to be skipped
                  nextSibling.dataset.levelSkip = 'true';
                  
                  // Add the next scale type to our extracted data
                  if (nextLevelType === 'confidence') {
                    extractedData.confidenceLevel = nextLevelValue.toLowerCase();
                } else {
                    if (!extractedData.scaleType2) {
                    extractedData.scaleType2 = nextLevelType;
                    extractedData.scaleLevel2 = nextLevelValue;
                } else {
                    extractedData.scaleType3 = nextLevelType;
                    extractedData.scaleLevel3 = nextLevelValue;
                }
                }
                  
                  setCombinedLevelData(extractedData);
                  setIsStandaloneLevelHeader(true);
                  return;
              }
            }
          }
          }
          
          if (Object.keys(extractedData).length > 0) {
            setIsStandaloneLevelHeader(true);
            setStandaloneLevelData(extractedData);
            return;
          }
        } else {
          console.warn('Missing required groups in H4 standalone level match:', { levelType, levelValue, groups: standaloneLevelMatch });
        }
      }

      // Check for parenthetical levels pattern like "(Impact Level: Very High, Confidence Level: High)"
      const parentheticalPattern = /^(.*?)\s*\(\s*(.+?)\s*\)\s*$/i;
      const parentheticalMatch = textContent.match(parentheticalPattern);
      
      if (parentheticalMatch) {
        const titlePart = parentheticalMatch[1].trim();
        const levelsPart = parentheticalMatch[2];
        
        // Check if the parentheses contain impact or confidence level information
        const hasImpactOrConfidence = /(?:Impact Level|Confidence Level|Contribution Level|Likelihood|Risk severity|Severity|Needs severity|Needs):/i.test(levelsPart);
        
        if (hasImpactOrConfidence) {
          // Extract individual levels
          const impactMatch = levelsPart.match(/Impact Level:\s*([^,]+)/i);
          const confidenceMatch = levelsPart.match(/Confidence Level:\s*([^,)]+)/i);
          const contributionMatch = levelsPart.match(/Contribution Level:\s*([^,)]+)/i);
          const likelihoodMatch = levelsPart.match(/Likelihood:\s*([^,)]+)/i);
          const riskSeverityMatch = levelsPart.match(/Risk severity:\s*([^,)]+)/i);
          const severityMatch = levelsPart.match(/Severity:\s*([^,)]+)/i);
          const needsSeverityMatch = levelsPart.match(/Needs severity:\s*([^,)]+)/i);
          const needsMatch = levelsPart.match(/Needs:\s*([^,)]+)/i);
          
          const extractedData = {};
          if (impactMatch) {
            extractedData.impactLevel = impactMatch[1].trim();
          }
          if (confidenceMatch) {
            extractedData.confidenceLevel = confidenceMatch[1].trim().toLowerCase();
          }
          if (contributionMatch) {
            extractedData.scaleType = 'contribution';
            extractedData.scaleLevel = contributionMatch[1].trim();
          }
          if (likelihoodMatch) {
            extractedData.scaleType = 'likelihood';
            extractedData.scaleLevel = likelihoodMatch[1].trim();
          }
          if (riskSeverityMatch) {
            extractedData.scaleType = 'risk severity';
            extractedData.scaleLevel = riskSeverityMatch[1].trim();
          }
          if (severityMatch) {
            extractedData.scaleType = 'severity';
            extractedData.scaleLevel = severityMatch[1].trim();
          }
          if (needsSeverityMatch) {
            extractedData.scaleType = 'needs severity';
            extractedData.scaleLevel = needsSeverityMatch[1].trim();
          }
          if (needsMatch) {
            extractedData.scaleType = 'needs';
            extractedData.scaleLevel = needsMatch[1].trim();
          }
          
          if (Object.keys(extractedData).length > 0) {
            setHasParentheticalLevels(true);
            setParentheticalData(extractedData);
            setCleanedTitle(titlePart);
            return;
          }
        }
      }

      // Check for raw HTML structure within H4 (similar to H3)
      const directChildDiv = element.querySelector(':scope > div.confidence-level-container');
      if (directChildDiv) {
        const classList = directChildDiv.className;
        const levelMatch = classList.match(/\b(high|medium|low)\b/i);
        if (levelMatch && levelMatch[1]) {
          setIsConfidenceLevel(true);
          setConfidenceLevel(levelMatch[1].toLowerCase());
          setIsRawHtmlConfidence(true);
          return; 
        }
      }

      // Pattern 1: Check id attribute like id="confidence-level-low" or "confidence-level--low"
      const idRegex = /^confidence-level(?:--|-)?(high|medium|low)$/i;
      const idMatch = idAttr.match(idRegex);

      if (idMatch && idMatch[1]) {
        setIsConfidenceLevel(true);
        setConfidenceLevel(idMatch[1].toLowerCase());
      } else {
        // If ID doesn't match, check content of the H4 for textual patterns
        const colonSyntaxContentRegex = /confidence level\s*(?:<\/[^>]+>\s*)?:\s*(high|medium|low)/i;
        const boldHyphenSyntaxContentRegex = /(?:<strong>|<b>)\s*Confidence level\s*[-–]\s*(high|medium|low)\s*(?:<\/strong>|<\/b>)/i;

        let contentMatch = htmlContent.match(colonSyntaxContentRegex);
        if (contentMatch && contentMatch[1]) {
          setIsConfidenceLevel(true);
          setConfidenceLevel(contentMatch[1].toLowerCase());
        } else {
          contentMatch = htmlContent.match(boldHyphenSyntaxContentRegex);
          if (contentMatch && contentMatch[1]) {
            setIsConfidenceLevel(true);
            setConfidenceLevel(contentMatch[1].toLowerCase());
          }
        }
      }
    }
  }, [children, node]);
  
  // If this element should be skipped (already rendered by previous impact level)
  if (shouldSkipRender) {
    return null;
  }
  
  // If this is a combined level header (impact + confidence), render both badges together
  if (combinedLevelData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
        {renderOrderedBadges(combinedLevelData, 'small')}
      </div>
    );
  }
  
  // If this is a standalone level header, render as badges
  if (isStandaloneLevelHeader && standaloneLevelData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
        {renderOrderedBadges(standaloneLevelData, 'small')}
      </div>
    );
  }
  
  
  if (hasParentheticalLevels && parentheticalData) {
    const parentNode = node?.parent;
    const grandparentNode = parentNode?.parent;
    const isInsideBlockquote = parentNode?.type === 'blockquote' || grandparentNode?.type === 'blockquote';
    let parentDivHasInlineBackground = false;

    const checkDivStyle = (divNode) => {
      if (divNode?.type === 'element' && divNode?.tagName?.toLowerCase() === 'div') {
        const styleProperty = divNode.properties?.style;
        if (typeof styleProperty === 'string' && styleProperty.length > 0) {
          const styleString = styleProperty.toLowerCase();
          if (styleString.includes('background-color') || styleString.includes('background:')) {
            return true;
          }
        }
      }
      return false;
    };

    if (checkDivStyle(parentNode)) {
      parentDivHasInlineBackground = true;
    } else if (checkDivStyle(grandparentNode) && parentNode?.type === 'element' && (parentNode?.tagName?.toLowerCase() === 'p' || parentNode?.tagName?.toLowerCase() === 'span')) {
      parentDivHasInlineBackground = true;
    }
    const omitStyles = isInsideSourcesSection || isInsideBlockquote || parentDivHasInlineBackground;

    const style = {
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      width: '100%', gap: '12px',
    };
    if (omitStyles) {
      style.marginTop = '0px'; style.paddingTop = '0px'; style.marginBottom = '6px';
    } else {
      style.marginTop = '0px'; style.paddingTop = '15px'; style.marginBottom = '12px'; style.borderTop = '1px solid #ececec';
    }
    return (
      <div style={style}>
        <h4 style={{ margin: '0', flex: '1', minWidth: '0' }} {...props} ref={h4Ref}>{cleanedTitle}</h4>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center' }}>{renderOrderedBadges(parentheticalData, 'small', true)}</div>
      </div>
    );
  }
  
  if (isConfidenceLevel && confidenceLevel) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <ConfidenceLevelWithSize level={confidenceLevel} transparent={true} size="small" />
      </div>
    );
  }
  
  return <h4 {...props} ref={h4Ref}>{children}</h4>;
};

// Custom h5 component to handle confidence levels in h5 tags (similar to CustomH4)
const CustomH5 = ({ children, isInsideSourcesSection = false, node, ...props }) => {
  const h5Ref = useRef(null);
  const [isConfidenceLevel, setIsConfidenceLevel] = useState(false);
  const [confidenceLevel, setConfidenceLevel] = useState(null);
  const [isRawHtmlConfidence, setIsRawHtmlConfidence] = useState(false);
  const [hasParentheticalLevels, setHasParentheticalLevels] = useState(false);
  const [parentheticalData, setParentheticalData] = useState(null);
  const [cleanedTitle, setCleanedTitle] = useState(children);
  const [isStandaloneLevelHeader, setIsStandaloneLevelHeader] = useState(false);
  const [standaloneLevelData, setStandaloneLevelData] = useState(null);
  const [shouldSkipRender, setShouldSkipRender] = useState(false);
  const [combinedLevelData, setCombinedLevelData] = useState(null);
  const theme = useMuiTheme();
  
  useEffect(() => {
    if (h5Ref.current) {
      const element = h5Ref.current;
      const htmlContent = element.innerHTML;
      const textContent = element.textContent || '';
      const idAttr = element.id || '';

      // Check if this element was already marked to be skipped
      if (element.dataset.levelSkip === 'true') {
        setShouldSkipRender(true);
        return;
      }

      // First check for standalone level headers like "Impact Level: High" or "Confidence Level: Medium"
      const standaloneLevelPattern = /^(Impact|Confidence|Contribution|Likelihood|Risk severity|Severity|Needs severity|Needs)\s+(level|score)\s*[:\-–]\s*(?:\d+\s*[-–]\s*)?(Very High|High|Medium|Low|Very Low|Very Likely|Likely|Moderately Likely|Unlikely|Very Unlikely|Critical|Severe|Moderate|Of Concern|None)$/i;
      
      const standaloneLevelMatch = textContent.match(standaloneLevelPattern);
      
      if (standaloneLevelMatch) {
        const levelType = standaloneLevelMatch[1]?.toLowerCase();
        const levelValue = standaloneLevelMatch[3]?.trim(); // Changed from [4] to [3]
        
        if (levelType && levelValue) {
          const extractedData = {};
          // Check if the next sibling is another level header that should be combined
          if (levelType === 'impact') {
            extractedData.impactLevel = levelValue;
          } else if (levelType === 'confidence') {
            extractedData.confidenceLevel = levelValue.toLowerCase();
          } else {
            extractedData.scaleType = levelType;
            extractedData.scaleLevel = levelValue;
          }
          
          
          // Check if the next sibling is another level header that should be combined
          if (levelType === 'impact') {
          const nextSibling = element.nextElementSibling;
            if (nextSibling && (nextSibling.tagName === 'H5' || nextSibling.tagName === 'H4' || nextSibling.tagName === 'H3')) {
              const nextText = nextSibling.textContent || '';
              const nextLevelMatch = nextText.match(standaloneLevelPattern);
              if (nextLevelMatch) {
                const nextLevelType = nextLevelMatch[1]?.toLowerCase();
                const nextLevelValue = nextLevelMatch[3]?.trim(); // Fixed from [4] to [3]
                
                if (nextLevelType && nextLevelValue) {
                  // Mark the next element to be skipped
                  nextSibling.dataset.levelSkip = 'true';
                  
                  // Add the next scale type to our extracted data
                  if (nextLevelType === 'confidence') {
                    extractedData.confidenceLevel = nextLevelValue.toLowerCase();
                } else {
                    if (!extractedData.scaleType2) {
                    extractedData.scaleType2 = nextLevelType;
                    extractedData.scaleLevel2 = nextLevelValue;
                } else {
                    extractedData.scaleType3 = nextLevelType;
                    extractedData.scaleLevel3 = nextLevelValue;
                }
                }
                  
                  setCombinedLevelData(extractedData);
                  setIsStandaloneLevelHeader(true);
                  return;
              }
            }
          }
          }
          
          if (Object.keys(extractedData).length > 0) {
            setIsStandaloneLevelHeader(true);
            setStandaloneLevelData(extractedData);
            return;
          }
        } else {
          console.warn('Missing required groups in H5 standalone level match:', { levelType, levelValue, groups: standaloneLevelMatch });
        }
      }

      // Check for parenthetical levels pattern like "(Impact Level: Very High, Confidence Level: High)"
      const parentheticalPattern = /^(.*?)\s*\(\s*(.+?)\s*\)\s*$/i;
      const parentheticalMatch = textContent.match(parentheticalPattern);
      
      if (parentheticalMatch) {
        const titlePart = parentheticalMatch[1].trim();
        const levelsPart = parentheticalMatch[2];
        
        // Check if the parentheses contain impact or confidence level information
        const hasImpactOrConfidence = /(?:Impact Level|Confidence Level|Contribution Level|Likelihood|Risk severity|Severity|Needs severity|Needs):/i.test(levelsPart);
        
        if (hasImpactOrConfidence) {
          // Extract individual levels
          const impactMatch = levelsPart.match(/Impact Level:\s*([^,]+)/i);
          const confidenceMatch = levelsPart.match(/Confidence Level:\s*([^,)]+)/i);
          const contributionMatch = levelsPart.match(/Contribution Level:\s*([^,)]+)/i);
          const likelihoodMatch = levelsPart.match(/Likelihood:\s*([^,)]+)/i);
          const riskSeverityMatch = levelsPart.match(/Risk severity:\s*([^,)]+)/i);
          const severityMatch = levelsPart.match(/Severity:\s*([^,)]+)/i);
          const needsSeverityMatch = levelsPart.match(/Needs severity:\s*([^,)]+)/i);
          const needsMatch = levelsPart.match(/Needs:\s*([^,)]+)/i);
          
          const extractedData = {};
          if (impactMatch) {
            extractedData.impactLevel = impactMatch[1].trim();
          }
          if (confidenceMatch) {
            extractedData.confidenceLevel = confidenceMatch[1].trim().toLowerCase();
          }
          if (contributionMatch) {
            extractedData.scaleType = 'contribution';
            extractedData.scaleLevel = contributionMatch[1].trim();
          }
          if (likelihoodMatch) {
            extractedData.scaleType = 'likelihood';
            extractedData.scaleLevel = likelihoodMatch[1].trim();
          }
          if (riskSeverityMatch) {
            extractedData.scaleType = 'risk severity';
            extractedData.scaleLevel = riskSeverityMatch[1].trim();
          }
          if (severityMatch) {
            extractedData.scaleType = 'severity';
            extractedData.scaleLevel = severityMatch[1].trim();
          }
          if (needsSeverityMatch) {
            extractedData.scaleType = 'needs severity';
            extractedData.scaleLevel = needsSeverityMatch[1].trim();
          }
          if (needsMatch) {
            extractedData.scaleType = 'needs';
            extractedData.scaleLevel = needsMatch[1].trim();
          }
          
          if (Object.keys(extractedData).length > 0) {
            setHasParentheticalLevels(true);
            setParentheticalData(extractedData);
            setCleanedTitle(titlePart);
            return;
          }
        }
      }

      // Check for raw HTML structure within H5
      const directChildDiv = element.querySelector(':scope > div.confidence-level-container');
      if (directChildDiv) {
        const classList = directChildDiv.className;
        const levelMatch = classList.match(/\b(high|medium|low)\b/i);
        if (levelMatch && levelMatch[1]) {
          setIsConfidenceLevel(true);
          setConfidenceLevel(levelMatch[1].toLowerCase());
          setIsRawHtmlConfidence(true);
          return; 
        }
      }

      // Pattern 1: Check id attribute like id="confidence-level-low" or "confidence-level--low"
      const idRegex = /^confidence-level(?:--|-)?(high|medium|low)$/i;
      const idMatch = idAttr.match(idRegex);

      if (idMatch && idMatch[1]) {
        setIsConfidenceLevel(true);
        setConfidenceLevel(idMatch[1].toLowerCase());
      } else {
        // If ID doesn't match, check content of the H5 for textual patterns
        const colonSyntaxContentRegex = /confidence level\s*(?:<\/[^>]+>\s*)?:\s*(high|medium|low)/i;
        const boldHyphenSyntaxContentRegex = /(?:<strong>|<b>)\s*Confidence level\s*[-–]\s*(high|medium|low)\s*(?:<\/strong>|<\/b>)/i;

        let contentMatch = htmlContent.match(colonSyntaxContentRegex);
        if (contentMatch && contentMatch[1]) {
          setIsConfidenceLevel(true);
          setConfidenceLevel(contentMatch[1].toLowerCase());
        } else {
          contentMatch = htmlContent.match(boldHyphenSyntaxContentRegex);
          if (contentMatch && contentMatch[1]) {
            setIsConfidenceLevel(true);
            setConfidenceLevel(contentMatch[1].toLowerCase());
          }
        }
      }
    }
  }, [children, node]);
  
  // If this element should be skipped (already rendered by previous impact level)
  if (shouldSkipRender) {
    return null;
  }
  
  // If this is a combined level header (impact + confidence), render both badges together
  if (combinedLevelData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: '6px', alignItems: 'center', marginBottom: '10px' }}>
        {renderOrderedBadges(combinedLevelData, 'small')}
      </div>
    );
  }
  
  // If this is a standalone level header, render as badges
  if (isStandaloneLevelHeader && standaloneLevelData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: '6px', alignItems: 'center', marginBottom: '10px' }}>
        {renderOrderedBadges(standaloneLevelData, 'small')}
      </div>
    );
  }
  
  if (hasParentheticalLevels && parentheticalData) {
    const parentNode = node?.parent;
    const grandparentNode = parentNode?.parent;
    const isInsideBlockquote = parentNode?.type === 'blockquote' || grandparentNode?.type === 'blockquote';
    let parentDivHasInlineBackground = false;

    const checkDivStyle = (divNode) => {
      if (divNode?.type === 'element' && divNode?.tagName?.toLowerCase() === 'div') {
        const styleProperty = divNode.properties?.style;
        if (typeof styleProperty === 'string' && styleProperty.length > 0) {
          const styleString = styleProperty.toLowerCase();
          if (styleString.includes('background-color') || styleString.includes('background:')) {
            return true;
          }
        }
      }
      return false;
    };

    if (checkDivStyle(parentNode)) {
      parentDivHasInlineBackground = true;
    } else if (checkDivStyle(grandparentNode) && parentNode?.type === 'element' && (parentNode?.tagName?.toLowerCase() === 'p' || parentNode?.tagName?.toLowerCase() === 'span')) {
      parentDivHasInlineBackground = true;
    }
    const omitStyles = isInsideSourcesSection || isInsideBlockquote || parentDivHasInlineBackground;

    const style = {
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      width: '100%', gap: '10px',
    };
    if (omitStyles) {
      style.marginTop = '0px'; style.paddingTop = '0px'; style.marginBottom = '5px';
    } else {
      style.marginTop = '0px'; style.paddingTop = '12px'; style.marginBottom = '10px'; style.borderTop = '1px solid #ececec';
    }
    return (
      <div style={style}>
        <h5 style={{ margin: '0', flex: '1', minWidth: '0' }} {...props} ref={h5Ref}>{cleanedTitle}</h5>
        <div style={{ display: 'flex', gap: '4px', flexShrink: 0, alignItems: 'center' }}>{renderOrderedBadges(parentheticalData, 'small', true)}</div>
      </div>
    );
  }
  
  if (isConfidenceLevel && confidenceLevel) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <ConfidenceLevelWithSize level={confidenceLevel} transparent={true} size="small" />
      </div>
    );
  }
  
  return <h5 {...props} ref={h5Ref}>{children}</h5>;
};

// Separate component for section content
const Section = memo(({ section, index, isLast, sectionRef }) => {
  const theme = useMuiTheme();
  const { currentTheme: appTheme } = useContext(AppThemeContext);
  const referencesTabRegex = /^(?:[\u{1F300}-\u{1FAFF}]\s*)?(References?|Sources?|Source List|Bibliograph(?:y|ies))$/iu;
  const isSourcesSection = referencesTabRegex.test(section.title.trim());
  
  const setRef = useCallback((el) => {
    sectionRef(el);
  }, [index, sectionRef]);

  // parse parenthetical scales in section title
  let titleText = section.title;
  let sectionLevels = null;
  const parentheticalMatch = section.title.match(/^(.*?)\s*\((.*)\)\s*$/);
  if (parentheticalMatch) {
    titleText = parentheticalMatch[1].trim();
    const levelsString = parentheticalMatch[2];
    const items = levelsString.split(/\s*,\s*/);
    const extractedLevels = {};
    items.forEach(item => {
      const m = item.match(/^(.*?)(?: level| score)?\s*[:\-–]\s*(.*)$/i);
      if (m) {
        const type = m[1].trim().toLowerCase();
        const value = m[2].trim();
        if (type.includes('impact')) {
          extractedLevels.impactLevel = value;
        } else if (type.includes('confidence')) {
          extractedLevels.confidenceLevel = value.toLowerCase();
        } else {
          if (!extractedLevels.scaleType) {
            extractedLevels.scaleType = type;
            extractedLevels.scaleLevel = value;
          } else if (!extractedLevels.scaleType2) {
            extractedLevels.scaleType2 = type;
            extractedLevels.scaleLevel2 = value;
          } else {
            extractedLevels.scaleType3 = type;
            extractedLevels.scaleLevel3 = value;
          }
        }
      }
    });
    sectionLevels = extractedLevels;
  }

  // extract numeric/text prefixes (Scenario/Strategic Objective/Topic/Risk) into a Chip
  const prefixMatch = titleText.match(/^(Scenario \d+|Strategic Objective \d+|Topic \d+|Risk \d+):\s*(.+)$/i);
  let sectionPreface = null;
  let isRiskPreface = false;
  if (prefixMatch) {
    sectionPreface = prefixMatch[1];
    isRiskPreface = /^Risk \d+/i.test(sectionPreface);
    titleText = prefixMatch[2];
  }

  return (
    <Box 
      className="section-container"
      id={`section-${index}`}
      ref={setRef}
      data-section-index={index}
      sx={{
        mb: isLast ? 0 : 2,
        scrollMarginTop: '20px',
        pb: 0,
        position: 'relative',
        ...(isSourcesSection && {
          backgroundColor: theme.palette.grey[50],
          padding: theme.spacing(1.5),
          borderRadius: theme.shape.borderRadius,
          marginTop: theme.spacing(1),
          marginBottom: isLast ? theme.spacing(1) : theme.spacing(2),
        })
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        width: '100%',
        gap: '12px',
        marginTop: '9px',
        paddingTop: '14px',
        marginBottom: '14px',
        borderBottom: '3px solid var(--color-primary)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flex: 1, minWidth: 0 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: 'var(--color-primary)',
              fontWeight: 700,
              fontSize: '16px', // was var(--font-size-md)
              wordWrap: 'break-word',
              flexShrink: 1,
              margin: 0,
              textTransform: 'uppercase'
            }}
          >
          {section.icon && section.icon}
          <span>
            {sectionPreface && <span style={{ color: isRiskPreface ? 'var(--color-error)' : 'var(--color-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>{sectionPreface}: </span>}
            {titleText}
          </span>
          </Typography>
        </Box>
        {(sectionLevels) && (
          <Box sx={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center' }}>
            {renderOrderedBadges(sectionLevels)}
          </Box>
        )}
      </Box>
      
      
      {section.isProblemTree ? (
        <ProblemTreeVisualization content={section.content} />
      ) : (
      <MarkdownPreview 
        source={replaceArrowEmojis(section.content, theme)}
        style={{ background: 'transparent' }}
        components={{
          p: CustomParagraph,
          h3: (h3Props) => <CustomH3 {...h3Props} isInsideSourcesSection={isSourcesSection} />,
          h4: (h4Props) => <CustomH4 {...h4Props} isInsideSourcesSection={isSourcesSection} />,
          h5: (h5Props) => <CustomH5 {...h5Props} isInsideSourcesSection={isSourcesSection} />,
          a: ({ node, ...props }) => {
            // Check if this is in the sources section
            const isSourceLink = props.href && props.href.startsWith('http');
            if (isSourceLink) {
              return (
                <a
                  {...props}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                }}
                />
              );
          }
            return <a {...props} />;
          },
          span: CustomSpan,
          table: ({node, ...props}) => (
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <table {...props} style={{ width: '100%', tableLayout: 'auto', wordWrap: 'break-word' }} />
            </div>
          )
        }}
      />
      )}
    </Box>
  );
}, (prevProps, nextProps) => {
  return prevProps.section.title === nextProps.section.title &&
         prevProps.section.content === nextProps.section.content &&
         prevProps.index === nextProps.index &&
         prevProps.isLast === nextProps.isLast;
});

// Create context for tab state management
const TabContext = createContext();

// Add this function before the AnalysisComponentWithTabs component (around line 2700)
export const processMainTitleConfidenceLevel = (title) => {
  if (!title || typeof title !== 'string') {
    return { cleanedTitle: title, confidenceLevelData: null };
  }

  // Check for parenthetical confidence level pattern like "(Confidence level: Medium)" or "(Confidence Level: High)"
  // This pattern supports various formats found in the codebase
  const parentheticalPattern = /^(.*?)\s*\(\s*Confidence\s+[Ll]evel\s*:\s*([^)]+)\s*\)\s*$/i;
  const match = title.match(parentheticalPattern);
  
  if (match) {
    const cleanedTitle = match[1].trim();
    const confidenceLevel = match[2].trim().toLowerCase();
    
    // Validate confidence level against known values from the codebase
    if (['high', 'medium', 'low', 'very high', 'very low'].includes(confidenceLevel)) {
      return {
        cleanedTitle,
        confidenceLevelData: { confidenceLevel }
      };
    }
  }
  
  // Also check for pattern without parentheses at the end of title like "Title Confidence Level: Medium"
  const directPattern = /^(.*?)\s+Confidence\s+[Ll]evel\s*:\s*(High|Medium|Low|Very\s+High|Very\s+Low)\s*$/i;
  const directMatch = title.match(directPattern);
  
  if (directMatch) {
    const cleanedTitle = directMatch[1].trim();
    const confidenceLevel = directMatch[2].trim().toLowerCase();
    
    return {
      cleanedTitle,
      confidenceLevelData: { confidenceLevel }
    };
  }
  
  return { cleanedTitle: title, confidenceLevelData: null };
};

const TabProvider = ({ children }) => {
  const [activeTab, setActiveTab] = useState(0);
  
  const value = useMemo(() => ({
    activeTab,
    setActiveTab
  }), [activeTab]);

  return (
    <TabContext.Provider value={value}>
      {children}
    </TabContext.Provider>
  );
};

// Separate component for folder tabs
const FolderTabs = memo(({ sections, onTabClick }) => {
  const { activeTab } = useContext(TabContext);
  // Regex to match references/sources tab
  const referencesTabRegex = /^(?:[\u{1F300}-\u{1FAFF}]\s*)?(References?|Sources?|Source List|Bibliograph(?:y|ies))$/iu;
  // Regex to strip leading emoji
  const leadingEmojiRegex = /^[\u{1F300}-\u{1FAFF}]\s*/u;
  const MAX_TITLE_LENGTH = 50; // Define a max length for titles

  return (
    <Box className="folder-tabs">
      {sections.map((section, index) => {
        const isReferencesTab = referencesTabRegex.test(section.title.trim());
        // strip out parenthetical scale info from titles
        let displayTitle = (() => {
          const parenMatch = section.title.match(/^(.*?)\s*\(.*\)\s*$/);
          return parenMatch ? parenMatch[1].trim() : section.title;
        })();
        if (isReferencesTab) {
          displayTitle = displayTitle.replace(leadingEmojiRegex, '');
        }

        // Truncate title if it's too long
        if (displayTitle.length > MAX_TITLE_LENGTH) {
          displayTitle = displayTitle.substring(0, MAX_TITLE_LENGTH - 3) + "...";
        }

        return (
          <button
            key={index}
            className={`folder-tab ${index === activeTab ? 'active' : ''}${isReferencesTab ? ' references-tab' : ''}`}
            onClick={() => onTabClick(index)}
            type="button"
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
              {isReferencesTab && <ManageSearchIcon sx={{ fontSize: '18px', marginRight: '2px', marginTop: '2px', writingMode: 'vertical-rl', transform: 'rotate(90deg)' }} />}
              {section.icon && !isReferencesTab && React.cloneElement(section.icon, { sx: { fontSize: '18px', marginRight: '2px', marginTop: '2px', writingMode: 'vertical-rl', transform: 'rotate(90deg)' } })}
              {displayTitle}
            </span>
          </button>
        );
      })}
    </Box>
  );
}, (prevProps, nextProps) => {
  return prevProps.sections === nextProps.sections;
});

// Content component that only handles rendering the content
const ContentComponent = memo(({ 
  sections, 
  folderContentRef, 
  sectionRefs, 
  handlePrint, 
  formattedLastUpdated, 
  viewId, 
  pillarId, 
  dateFieldRef,
  // Navigation props
  handlePreviousAnalysis,
  handleNextAnalysis,
  currentHistoryIndex,
  analysisHistory,
  onNavigateToAnalysis,
  handleHistoryMenuOpen,
  historyMenuAnchor,
  handleHistoryMenuClose,
  handleSelectHistoryItem,
  handleViewAnalysisInAdmin,
  handleDeleteAnalysis,
  handleRegenerateAnalysis,
  handleEditPillar,
  isSuperAdmin,
  data
}) => {
  const theme = useMuiTheme();
  const { currentTheme: appTheme } = useContext(AppThemeContext);
  const { isProcessing: isSchedulerProcessing } = useScheduledAnalysis(viewId, pillarId, { taskTypes: ['analysis'], enableScheduleFetch: false });
  const { user } = useAuth();
  const hideExport = useMemo(() => {
    // Authenticated users always get the export button
    if (user) return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('hideExport') === 'true'
      || params.get('orgIframe') === 'true'
      || window.location.pathname.startsWith('/public/');
  }, [user]);
  const referencesTabRegex = /^(?:[\u{1F300}-\u{1FAFF}]\s*)?(References?|Sources?|Source List|Bibliograph(?:y|ies))$/iu;
  const isFirstSectionSources = sections.length > 0 && referencesTabRegex.test(sections[0].title.trim());

  // Debug logging removed for performance

  const memoizedSections = useMemo(() => sections.map((section, index) => (
    <Section
      key={index}
      section={section}
      index={index}
      isLast={index === sections.length - 1}
      sectionRef={el => { sectionRefs.current[index] = el; }}
    />
  )), [sections, sectionRefs]);

  return (
    <Box 
      className="folder-content" 
      ref={folderContentRef}
      sx={{ 
        overflowX: 'hidden', // Prevent horizontal scrolling on the main content area
        // Ensure other necessary styles like height/overflowY are maintained as per your existing setup
        // For example, if it's meant to scroll vertically:
        height: '100%', // Or a calculated height
        overflowY: 'auto' // Or 'scroll'
      }}
    >
      <Fade in={true}>
        <Box className="continuous-content">
          {memoizedSections.length > 0 && (
            <Box 
              className="section-container"
              id={`section-0`}
              ref={el => { sectionRefs.current[0] = el; }}
              data-section-index={0}
              sx={{
                mb: sections.length > 1 ? 2 : 0, // Adjust margin if it's the only section
                scrollMarginTop: '20px',
                pb: 0,
                position: 'relative',
                ...(sections.length > 0 && referencesTabRegex.test(sections[0].title.trim()) && {
                  backgroundColor: theme.palette.grey[50],
                  padding: theme.spacing(1.5),
                  borderRadius: theme.shape.borderRadius,
                  marginTop: theme.spacing(1),
                  marginBottom: sections.length > 1 ? theme.spacing(2) : theme.spacing(1),
              })
            }}
            >
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 0,
                mb: 0.75
            }}>
                {(() => {
                  const { cleanedTitle, confidenceLevelData } = processMainTitleConfidenceLevel(sections[0].title);
                  return (
                    <>
                      <Typography 
                        variant="h4" 
                        component="h1" 
                        sx={{ 
                          color: 'var(--color-primary)',
                          fontWeight: 700,
                          fontSize: '16px', // was var(--font-size-md)
                          lineHeight: 1.05,
                          wordWrap: 'break-word',
                          flexShrink: 1,
                          margin: 0,
                          textTransform: 'uppercase',
                          width: '100%'
                      }}
                      >
                        {cleanedTitle}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 1,
                        flexWrap: 'wrap',
                        mt: -0.25,
                        flexShrink: 0
                      }}>
                        {confidenceLevelData && confidenceLevelData.confidenceLevel && (
                          <ConfidenceLevelWithSize 
                            level={confidenceLevelData.confidenceLevel} 
                            size="normal"
                          />
                        )}
                        {/* Timestamp with Navigation, Download, and Timer chips */}
                        {formattedLastUpdated && (
                          <>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 0.25,
                              backgroundColor: 'rgba(0, 0, 0, 0.03)',
                              borderRadius: '12px',
                              paddingLeft: 0.5,
                              paddingRight: 0.5,
                              paddingTop: 0.25,
                              paddingBottom: 0.25,
                              mb: 0.5,
                              border: '1px solid',
                              borderColor: 'rgba(0, 0, 0, 0.05)',
                              width: 'fit-content'
                            }}>
                              {/* Previous analysis button */}
                              <IconButton
                                size="small"
                                onClick={handlePreviousAnalysis}
                                disabled={currentHistoryIndex >= analysisHistory.length - 1 || !onNavigateToAnalysis}
                                sx={{ 
                                  p: 0.25,
                                  opacity: currentHistoryIndex >= analysisHistory.length - 1 ? 0.3 : 0.6,
                                  '&:hover': { opacity: 1 }
                                }}
                              >
                                <ChevronLeftIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                              </IconButton>
                              
                              {/* Date display with dropdown trigger */}
                              <Box
                                onClick={handleHistoryMenuOpen}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  cursor: analysisHistory.length > 1 ? 'pointer' : 'default',
                                  px: 0.5,
                                  '&:hover': analysisHistory.length > 1 ? {
                                    '& .expand-icon': { opacity: 1 }
                                  } : {}
                                }}
                              >
                                {isSchedulerProcessing ? (
                                  <CircularProgress
                                    size={14}
                                    thickness={5}
                                    sx={{ color: 'text.secondary', opacity: 0.7 }}
                                  />
                                ) : (
                                  <AccessTimeIcon sx={{ fontSize: '1rem', color: 'text.secondary', opacity: 0.7 }} />
                                )}
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: 'text.secondary',
                                    fontSize: 'var(--font-size-sm)',
                                    opacity: 0.7,
                                    fontStyle: 'italic'
                                  }}
                                >
                                  {formattedLastUpdated}
                                </Typography>
                                {analysisHistory.length > 1 && (
                                  <ExpandMoreIcon 
                                    className="expand-icon"
                                    sx={{ 
                                      fontSize: '1rem', 
                                      color: 'text.secondary', 
                                      opacity: 0.5,
                                      transition: 'opacity 0.2s'
                                    }} 
                                  />
                                )}
                              </Box>
                              
                              {/* Next analysis button */}
                              <IconButton
                                size="small"
                                onClick={handleNextAnalysis}
                                disabled={currentHistoryIndex <= 0 || !onNavigateToAnalysis}
                                sx={{ 
                                  p: 0.25,
                                  opacity: currentHistoryIndex <= 0 ? 0.3 : 0.6,
                                  '&:hover': { opacity: 1 }
                                }}
                              >
                                <ChevronRightIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                              </IconButton>
                            </Box>
                            
                            {/* History dropdown menu */}
                            <Menu
                              anchorEl={historyMenuAnchor}
                              open={Boolean(historyMenuAnchor)}
                              onClose={handleHistoryMenuClose}
                              PaperProps={{
                                sx: {
                                  maxHeight: 400,
                                  width: isSuperAdmin ? 320 : 240,
                                  mt: 0.5
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.5, py: 0.5 }}>
                                <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.7rem', p: 0, minHeight: 'auto' }}>
                                  Analysis History ({analysisHistory.length})
                                </MenuItem>
                                {isSuperAdmin && (
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton 
                                      size="small" 
                                      onClick={handleEditPillar}
                                      sx={{ 
                                        p: 0.25, 
                                        '& svg': { fontSize: '1rem' },
                                        display: 'flex',
                                        gap: 0.5,
                                        fontSize: '0.7rem',
                                        '& span': { color: 'text.secondary' }
                                      }}
                                      color="primary"
                                    >
                                      <TerminalIcon />
                                      <span>Edit Prompts</span>
                                    </IconButton>
                                    <IconButton 
                                      size="small" 
                                      onClick={handleRegenerateAnalysis}
                                      sx={{ 
                                        p: 0.25, 
                                        '& svg': { fontSize: '1rem' },
                                        display: 'flex',
                                        gap: 0.5,
                                        fontSize: '0.7rem',
                                        '& span': { color: 'text.secondary' }
                                      }}
                                      color="primary"
                                    >
                                      <AutoModeIcon />
                                      <span>Generate New</span>
                                    </IconButton>
                                  </Box>
                                )}
                              </Box>
                              <Divider />
                              {analysisHistory.map((item, index) => {
                                const itemDate = new Date(item.completed_at || item.updated_at || item.created_at);
                                const isCurrentItem = item.id === data?.id;
                                
                                return (
                                  <MenuItem
                                    key={item.id}
                                    onClick={() => handleSelectHistoryItem(item.id)}
                                    selected={isCurrentItem}
                                    sx={{
                                      py: 0.95,
                                      px: 1.5,
                                      minHeight: 'unset',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      backgroundColor: isCurrentItem ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                                      '&:hover': {
                                        backgroundColor: isCurrentItem ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                                      }
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                      <Typography variant="body2" sx={{ fontWeight: isCurrentItem ? 600 : 400, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                                        {format(itemDate, 'MMM d, yyyy')}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem', lineHeight: 1.2 }}>
                                        {format(itemDate, 'h:mm a')} • {formatDistanceToNow(itemDate, { addSuffix: true }).replace('about ', '')}
                                      </Typography>
                                    </Box>

                                    {isSuperAdmin && (
                                      <Box sx={{ display: 'flex', gap: 0.25, ml: 0.75 }}>
                                        <Tooltip title="View in Admin">
                                          <IconButton 
                                            size="small" 
                                            onClick={(e) => handleViewAnalysisInAdmin(e, item.id)}
                                            sx={{ p: 0.375, '& svg': { fontSize: '1rem' } }}
                                          >
                                            <VisibilityIcon />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                          <IconButton 
                                            size="small" 
                                            color="error"
                                            onClick={(e) => handleDeleteAnalysis(e, item.id)}
                                            sx={{ p: 0.375, '& svg': { fontSize: '1rem' } }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    )}
                                  </MenuItem>
                                );
                              })}
                            </Menu>
                          </>
                        )}
                        {!hideExport && (
                          <Tooltip title="Export / Print Analysis" placement="bottom-end">
                            <IconButton 
                              onClick={handlePrint} 
                              size="small" 
                              sx={{ 
                                p: '5px',
                                display: { xs: 'none', sm: 'inline-flex' } // Hide on mobile
                              }}
                            >
                              <DownloadIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </>
                  );
                })()}
              </Box>
              
              {sections[0].isProblemTree ? (
                <ProblemTreeVisualization content={sections[0].content} />
              ) : (
              <MarkdownPreview 
                source={replaceArrowEmojis(sections[0].content, theme)}
                style={{ background: 'transparent' }}
                components={{
                  p: CustomParagraph,
                  h3: (h3Props) => <CustomH3 {...h3Props} isInsideSourcesSection={isFirstSectionSources} />,
                  h4: (h4Props) => <CustomH4 {...h4Props} isInsideSourcesSection={isFirstSectionSources} />,
                  h5: (h5Props) => <CustomH5 {...h5Props} isInsideSourcesSection={isFirstSectionSources} />,
                  a: ({ node, ...props }) => {
                    // Check if this is in the sources section
                    const isSourceLink = props.href && props.href.startsWith('http');
                    if (isSourceLink) {
                      return (
                        <a
                          {...props}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            pointerEvents: 'auto',
                            cursor: 'pointer'
                        }}
                        />
                      );
                  }
                    return <a {...props} />;
                },
                  span: CustomSpan,
                  table: ({node, ...props}) => (
                    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                      <table {...props} style={{ width: '100%', tableLayout: 'auto', wordWrap: 'break-word' }} />
                    </div>
                  )
              }}
              />
              )}
            </Box>
          )}
          
          {memoizedSections.slice(1)}
        </Box>
      </Fade>
    </Box>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.sections === nextProps.sections &&
    prevProps.formattedLastUpdated === nextProps.formattedLastUpdated &&
    prevProps.viewId === nextProps.viewId &&
    prevProps.pillarId === nextProps.pillarId &&
    prevProps.currentHistoryIndex === nextProps.currentHistoryIndex &&
    prevProps.analysisHistory === nextProps.analysisHistory &&
    prevProps.isSuperAdmin === nextProps.isSuperAdmin &&
    prevProps.historyMenuAnchor === nextProps.historyMenuAnchor
  );
});

// Scroll tracking component that only handles intersection observer
const ScrollTracker = memo(({ sections, folderContentRef, sectionRefs, programmaticScrollRef }) => {
  const observerRef = useRef(null);
  const activeTabRef = useRef(null);
  const lastActiveTabRef = useRef(null);
  const { setActiveTab } = useContext(TabContext);
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    if (!sections || sections.length === 0) return;
    
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.sectionIndex, 10);
            if (!isNaN(index) && index !== lastActiveTabRef.current) {
              // Skip tab updates during programmatic scrolling
              if (programmaticScrollRef.current) {
                return;
            }

              // Clear any pending debounce timeout
              if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }

              // Debounce the tab update
              debounceTimeoutRef.current = setTimeout(() => {
                activeTabRef.current = index;
                lastActiveTabRef.current = index;
                setActiveTab(index);
            }, 150); // Increased debounce for better performance
          }
          }
        });
      },
      {
        root: folderContentRef.current,
        rootMargin: '-20% 0px -80% 0px',
        threshold: [0, 0.5] // Reduced thresholds for better performance
      }
    );

    sectionRefs.current.forEach((sectionRef) => {
      if (sectionRef) {
        observerRef.current.observe(sectionRef);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [sections, setActiveTab, folderContentRef, sectionRefs, programmaticScrollRef]);

  return null;
}, (prevProps, nextProps) => {
  return prevProps.sections === nextProps.sections;
});

/**
 * Component to display analysis text for a view and theme
 */
const AnalysisComponentImpl = memo(({ data, isLoading, error, viewName, pillarName, subPillarName, subPillarId, onNavigateToAnalysis }) => {
  // Component name for debugging
  AnalysisComponentImpl.displayName = 'AnalysisComponentImpl';
  
  // Get MUI theme for responsive design
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  // -------------------------------------------------------------
  // Restore "Download as Markdown" functionality
  // -------------------------------------------------------------
  const handlePrint = () => {
    window.print();
  };

  // Helper function to clean text parts (colons, arrows)
  const doCleanTextPart = (text) => {
    if (typeof text !== 'string') return '';
    return text.replace(/:|->|=>|→|⇒|&rarr;|--&gt;|-&gt;/g, '').trim();
  };

  // Format the last updated time in a human-readable way with dynamic updates
  const [formattedLastUpdated, setFormattedLastUpdated] = useState(null);
  const dateFieldRef = useRef(null);
  
  // Analysis history navigation state
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(0);
  const [historyMenuAnchor, setHistoryMenuAnchor] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [analysisToDelete, setAnalysisToDelete] = useState(null);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  
  // Auth context for super admin features
  const { user } = useAuth();
  const isSuperAdmin = user?.is_admin || user?.role === 'super_admin' || user?.organization_role === 'super_admin';
  
  // Setup refs for each section
  const sectionRefs = useRef([]);
  const folderContentRef = useRef(null);
  const programmaticScrollRef = useRef(false);
  const { setActiveTab } = useContext(TabContext);

  // Function to remove links from markdown
  const removeLinks = (content) => {
    return content.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  };

  // Function to remove main title if it exists
  const removeMainTitle = (content) => {
    return content
      .replace(/^# [^\n]+\n/, '')
      .replace(/^\*\*[^\n]+\*\*\n=+\n/, '')
      .replace(/^\n+/, '')
      .trim();
  };

  // Function to convert all header styles to # style
  const normalizeHeaders = (content) => {
    // Convert headers with underlines to # style
    content = content.replace(/^([^\n]+)\n=+\n/gm, '# $1\n');
    content = content.replace(/^([^\n]+)\n-+\n/gm, '## $1\n');
    
    // Remove bold markers from headers
    content = content.replace(/^(#{1,6})\s+\*\*(.*?)\*\*/gm, '$1 $2');
    
    return content;
  };

  // Function to standardize markdown headers
  const standardizeHeaders = (content) => {
    content = normalizeHeaders(content);
    const lines = content.split('\n');
    
    // Keep original header levels without adjustment
    return lines.map(line => {
      const headerMatch = line.match(/^(#{1,6})\s(.*)/);
      if (!headerMatch) return line;

      const headerText = headerMatch[2].trim();
      const level = headerMatch[1].length;
      
      return '#'.repeat(level) + ' ' + headerText;
    }).join('\n');
  };

  // Function to parse sources section into a map
  const parseSources = (content) => {
    const sourceMap = new Map();
    // Revised regex to correctly capture headers like "## 11. Bibliography"
    const sourcesHeaderRegex = /(?:^|\n)(##?\s*(?:[\d\.]+\s*)?(?:[\u{1F300}-\u{1FAFF}]\s*)?(?:Sources?|Source List|Bibliograph(?:y|ies)|References?)[^\n]*\n)([\s\S]*?)(?=\n##|\n$|$)/iu;
    const sourcesMatch = content.match(sourcesHeaderRegex);

    if (!sourcesMatch) {
      return sourceMap;
    }

    const sourcesContent = sourcesMatch[2].trim();
    
    // Process non-empty lines from the sources content
    const lines = sourcesContent.split('\n').filter(line => line.trim() !== '');

    for (const line of lines) {
      let number = null;
      let citationText = null;

      // Attempt to match format: [number] Citation text
      // Example: [1] Reuters (04/04/2025)...
      // Regex: Starts with optional whitespace, then [number], then whitespace, then captures the rest.
      const matchFormat1 = line.match(/^\s*\[(\d+)\]\s*(.+)/);
      if (matchFormat1) {
        number = matchFormat1[1];
        citationText = matchFormat1[2];
      } else {
        // Attempt to match format: (Optional list marker) Citation text [number]
        // Example: 1. Reuters (04/04/2025)... [1]
        // Example: * Reuters (04/04/2025)... [1]
        // Example: Reuters (04/04/2025)... [1]
        // Regex: Optional list marker (digits/dots/stars/hyphens followed by space), 
        // then captures text (non-greedy), then captures [number] at the end of the line.
        const matchFormat2 = line.match(/^(?:[\d\.\*\-]+\s*)*(.+?)\s*\[(\d+)\]\s*$/);
        if (matchFormat2) {
          number = matchFormat2[2]; 
          citationText = matchFormat2[1];
        }
      }

      if (number && citationText && !sourceMap.has(number)) {
        // Split citation into main text and description if "Description:" is present
        const [mainCitation, ...descriptionParts] = citationText.trim().split(/\s*Description:\s*/);
        sourceMap.set(number, {
          citation: mainCitation.trim(),
          description: descriptionParts.length > 0 ? descriptionParts.join(' ').trim() : null,
        });
      }
    }
    
    return sourceMap;
  };

  // Function to format citation text for sources section (with HTML)
  const formatCitationForSourcesSection = (citation) => {
    // First, remove any pre-existing markdown links to avoid duplication/mangling
    const textWithoutMarkdownLinks = removeLinks(citation);

    // Convert markdown italics to HTML italics for sources section
    let processedCitation = textWithoutMarkdownLinks.replace(/\*([^*]+)\*/g, '<i>$1</i>');

    let finalUrl = null;
    let textToParse = processedCitation;

    // Try to extract URL from the end of the processedCitation
    const R_URL_AT_END = /(https?:\/\/[^\s<"]+)(?:\s*<\/[^>]+>|\s*)*$/;
    const urlMatchAtEnd = processedCitation.match(R_URL_AT_END);
    if (urlMatchAtEnd) {
      finalUrl = urlMatchAtEnd[1];
      textToParse = processedCitation.substring(0, urlMatchAtEnd.index).trim();
    }

    // Attempt "new format" (comma-separated) parsing on `textToParse`
    if (textToParse.includes(',')) {
      const parts = [];
      let currentPart = '';
      let insideQuotes = false;
      for (let i = 0; i < textToParse.length; i++) {
        const char = textToParse[i];
        if (char === '"') {
          insideQuotes = !insideQuotes;
          currentPart += char;
        } else if (char === ',' && !insideQuotes) {
          parts.push(currentPart.trim());
          currentPart = '';
        } else {
          currentPart += char;
        }
      }
      if (currentPart.trim()) {
        parts.push(currentPart.trim());
      }
      
      const sourceName = doCleanTextPart(parts[0]);
      const year = doCleanTextPart(parts[1]);
      let title = parts[2]?.trim()?.replace(/^"|"$/g, '') || '';
      title = doCleanTextPart(title);

      const formattedSource = sourceName ? `<strong style="color: var(--color-text-primary); font-family: var(--font-family-base)">${sourceName}</strong>` : '';
      const formattedYear = year ? `<span style="color: #000; font-family: var(--font-family-base)"> (${year})</span>` : '';
      const formattedTitle = title ? `<span style="color: #000; font-family: var(--font-family-base)"> <i>${title}</i></span>` : '';
      
      let formattedUrl = '';
      if (finalUrl) {
        const cleanedDisplayUrl = cleanUrl(finalUrl);
        formattedUrl = `<a href="${cleanedDisplayUrl}" target="_blank" rel="noopener noreferrer"><span style='font-size: var(--font-size-xs); display: block; margin-top: 4px; font-family: var(--font-family-base)'>${cleanedDisplayUrl}</span></a>`;
      }
      
      const result = `${formattedSource}${formattedYear}${formattedTitle}${formattedUrl}`.trim();
      return result || doCleanTextPart(processedCitation);
    }
    
    // Handle the "old format"
    const stringForOldFormatParse = finalUrl ? textToParse : processedCitation;
    const R_OLD_FORMAT = /(.*?)(\s*\([^)]+\))(.*?)(?:((?:https?:\/\/[^\s<"]+))(?:\s*(?:<\/[^>]+>)*\s*$|\s*$)|$)/;
    const oldFormatMatch = stringForOldFormatParse.match(R_OLD_FORMAT);

    if (oldFormatMatch) {
      const [, rawSourceName, rawDateParens, rawAdditionalText, potentialUrlFromRegex] = oldFormatMatch;

      const sourceName = doCleanTextPart(rawSourceName);
      const dateParens = rawDateParens || ""; 
      const additionalText = doCleanTextPart(rawAdditionalText);
      
      const actualUrl = finalUrl || (potentialUrlFromRegex ? potentialUrlFromRegex : null);
      const displayUrl = actualUrl ? cleanUrl(actualUrl) : null;

      const formattedSource = sourceName ? `<strong style="color: var(--color-text-primary); font-family: var(--font-family-base)">${sourceName}</strong>` : '';
      const formattedRest = (dateParens.trim() || additionalText.trim()) ? `${dateParens}${additionalText}` : '';
      
      let formattedUrl = '';
      if (displayUrl) {
        formattedUrl = `<a href="${displayUrl}" target="_blank" rel="noopener noreferrer"><span style='font-size: var(--font-size-xs); display: block; margin-top: 4px; font-family: var(--font-family-base)'>${displayUrl}</span></a>`;
      }
      
      const result = `${formattedSource}${formattedRest}${formattedUrl}`.trim();
      return result || doCleanTextPart(processedCitation);
    }

    // Fallback
    let result = doCleanTextPart(textToParse);
    if (finalUrl) {
      const cleanedDisplayUrl = cleanUrl(finalUrl);
      result += `<a href="${cleanedDisplayUrl}" target="_blank" rel="noopener noreferrer"><span style='font-size: var(--font-size-xs); display: block; margin-top: 4px; font-family: var(--font-family-base)'>${cleanedDisplayUrl}</span></a>`;
    }
    
    return result || doCleanTextPart(processedCitation);
  };

  // Function to replace citations with tooltips
  const replaceCitationsWithTooltips = (content, sourceMap) => {
    // Only match [number] patterns that:
    // 1. Are not preceded by a newline and space (to avoid source definitions)
    // 2. Are not part of a heading pattern (# or ##)
    return content.replace(/(?<!(?:\n\s*|#+ ))\[(\d+(?:,\s*\d+)*)\]/g, (match, numbers, offset) => {
      // Check if this citation is within a markdown table
      const beforeMatch = content.substring(0, offset);
      const afterMatch = content.substring(offset);
      
      // Look for table structure around this citation
      const lineStart = beforeMatch.lastIndexOf('\n') + 1;
      const lineEnd = afterMatch.indexOf('\n');
      const currentLine = content.substring(lineStart, offset + match.length + (lineEnd === -1 ? 0 : lineEnd));
      
      // Check if current line contains table pipes (indicating it's in a table)
      const isInTable = currentLine.includes('|') && /\|.*\|/.test(currentLine);
      
      if (isInTable) {
        // For tables, use a contained format that preserves functionality but stays atomic
        const tableCitations = numbers.split(/,\s*/).map(num => {
          const source = sourceMap.get(num);
          if (!source) return `<span class="citation" style="cursor: pointer; color: var(--color-primary);" data-tooltip="Source not found">${num}</span>`;
          
          const formattedCitation = formatCitationForSourcesSection(source.citation);
          
          let cleanedDescription = '';
          if (source.description) {
            // Clean colons and various arrow types from the description
            cleanedDescription = source.description.replace(/:|->|=>|→|⇒|&rarr;|--&gt;|-&gt;/g, '');
          }
          
          const tooltipText = cleanedDescription 
            ? `<span class='citation'>${num}</span> ${formattedCitation}\n${cleanedDescription}`
            : `<span class='citation'>${num}</span> ${formattedCitation}`;
          
          // Ensure the tooltip content doesn't contain pipe characters that could break tables
          const tableSafeTooltip = tooltipText.replace(/\|/g, '');
          const escapedTooltip = escapeHtmlAttribute(tableSafeTooltip);
          
          return `<span class="citation" data-tooltip="${escapedTooltip}" style="cursor: pointer; color: var(--color-primary);"><span>${num}</span></span>`;
        });
        
        return tableCitations.join('');
      } else {
        // For non-table content, use the full formatting
        const citations = numbers.split(/,\s*/).map(num => {
          const source = sourceMap.get(num);
          if (!source) return `<span class='citation'>${num}</span> Source not found`;
          
          const formattedCitation = formatCitationForSourcesSection(source.citation);
          
          let cleanedDescription = '';
          if (source.description) {
            // Clean colons and various arrow types from the description
            cleanedDescription = source.description.replace(/:|->|=>|→|⇒|&rarr;|--&gt;|-&gt;/g, '');
          }
          
          const tooltipText = cleanedDescription 
            ? `<span class='citation'>${num}</span> ${formattedCitation}\n${cleanedDescription}`
            : `<span class='citation'>${num}</span> ${formattedCitation}`;
          
          return tooltipText;
        });

        // Format numbers as individual spans
        const formattedNumbers = numbers.split(/,\s*/)
          .map(num => `<span>${num}</span>`)
          .join('');
        
        // Use HTML data attribute to store tooltip content
        const tooltipContent = `<span>${citations.join('\\n\\n')}</span>`;
        const escapedTooltip = escapeHtmlAttribute(tooltipContent);
        return `<span class="citation" data-tooltip="${escapedTooltip}">${formattedNumbers}</span>`;
      }
    });
  };

  // Function to clean header titles (remove numbers and dots at start)
  const cleanHeaderTitle = (title) => {
    return title.replace(/^\d+\.?\s*/, '').trim();
  };
  
  // MODIFIED formatSourcesSection to use sourceMap
  const formatSourcesSection = (content, sourceMap) => {
    // Revised regex to correctly capture headers like "## 11. Bibliography"
    const sourcesHeaderRegex = /(?:^|\n)(##?\s*(?:[\d\.]+\s*)?(?:[\u{1F300}-\u{1FAFF}]\s*)?(?:Sources?|Source List|Bibliograph(?:y|ies)|References?)[^\n]*\n)([\s\S]*?)(?=\n##|\n$|$)/iu;
    const sourcesMatch = content.match(sourcesHeaderRegex);

    if (!sourcesMatch) {
      // If the header isn't found, return content as is.
      return content;
    }
    if (sourceMap.size === 0) {
      // If header found but no sources parsed by parseSources, 
      // return content to avoid deleting a potentially non-empty unparsed section.
      return content; 
    }

    const originalHeaderWithNewline = sourcesMatch[1]; // e.g., "## 11. Bibliography\n"
    
    const formattedSourceEntries = [];
    const sortedSourceNumbers = Array.from(sourceMap.keys()).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

    for (const number of sortedSourceNumbers) {
      const sourceData = sourceMap.get(number);
      if (sourceData && typeof sourceData.citation === 'string') {
        const formattedHtmlDetails = formatCitationForSourcesSection(sourceData.citation);
        formattedSourceEntries.push(
          `<p><span class="citation" style="color: var(--color-primary); cursor: pointer;">${number}</span> ${formattedHtmlDetails}</p>`
        );
      }
    }
    
    const finalSourcesHtmlBlock = formattedSourceEntries.join('\n');

    // Replace the original bibliography section (header + old markdown content)
    const result = content.replace(sourcesHeaderRegex, `${originalHeaderWithNewline}${finalSourcesHtmlBlock}\n`);
    return result;
  };

  // Function to extract and remove section-level confidence statements from markdown
  const extractAndRemoveSectionConfidence = useCallback((markdownContent) => {
    // Return content as-is without extracting confidence levels
    return { level: null, content: markdownContent };
  }, []);

  // Extract content from data, with fallbacks for different data structures
  const extractContent = useMemo(() => {
    if (!data) return null;
    
    // If data has content property, use it directly
    if (data.content) return data.content;
    
    // If data has text or analysis property, try those
    if (data.text) return data.text;
    if (data.analysis) return data.analysis;
    
    // If data has a description property, use that
    if (data.description) return data.description;
    
    // If data is an object with keys that might contain content
    if (typeof data === 'object') {
      // Look for any property that might contain content
      const contentProps = ['content', 'text', 'analysis', 'description', 'summary'];
      for (const key of Object.keys(data)) {
        if (typeof data[key] === 'string' && data[key].length > 100) {
          console.log(`Using ${key} property as content:`, data[key]);
          return data[key];
        }
      }
    }
    
    return null;
  }, [data]);

  // Split content into sections based on H2 headers (after H1)
  const sections = useMemo(() => {
    const extractedContent = extractContent;
    
    if (!extractedContent) {
      return [{
        title: 'Error',
        content: 'Unable to display analysis content. The data received is not in the expected format.'
      }];
    }

    const sourceMap = parseSources(extractedContent); 
    
    let workingContent = extractedContent;
    workingContent = replaceCitationsWithTooltips(workingContent, sourceMap);
    
    workingContent = formatSourcesSection(workingContent, sourceMap);
    
    workingContent = standardizeHeaders(removeLinks(workingContent));
        
    let isExecutiveSummaryBlockquotedAndHrRemoved = false;

    // Special case for Executive Summary: blockquote its content and remove following HR if conditions are met.
    // Regex targets: Optional H1, then H2 "Executive Summary" (or variants), its content, then a mandatory "\n---\n"
    const executiveSummaryPattern = /^((?:#\s[^\n]+\n\s*)?)(##\s*(?:Executive Summary|Overview|Summary)\s*\n)([\s\S]*?)(\n---\n)/m;
    // Group 1: Optional H1 (e.g., "# Title\n") - includes optional trailing whitespace for H1
    // Group 2: H2 Executive Summary Header (e.g., "## Executive Summary\n")
    // Group 3: Content of Executive Summary
    // Group 4: The "\n---\n" to be removed

    workingContent = workingContent.replace(executiveSummaryPattern, (match, optionalH1, execSummaryHeader, execSummaryContent, hrMarker) => {
      const trimmedExecContent = execSummaryContent.trim();
      const isFlat = !/^#{3,6} /m.test(trimmedExecContent);
      const isSinglePara = !trimmedExecContent.includes('\n\n');

      if (isFlat && isSinglePara) {
        isExecutiveSummaryBlockquotedAndHrRemoved = true;
        const blockquotedContent = '> ' + trimmedExecContent.split('\n').join('\n> ');
        // Return H1 (if exists) + H2 header + blockquoted content + a single newline (replacing the HR structure)
        return (optionalH1 || '') + execSummaryHeader + blockquotedContent + '\n'; 
      }
      return match; // No change, keep original including HR
    });

    // Check if there's any H2 headers in the content
    const hasH2Headers = /^## /m.test(workingContent);
    const hasH3Headers = /^### /m.test(workingContent);
    
    // Special case: Check if this is a PHASE analysis pattern (H2 title followed by H3 PHASE sections)
    const isPhaseAnalysis = workingContent.match(/^## .*PHASE.*Analysis/i) && 
                           workingContent.match(/^### PHASE \d+/m);
    
    if (isPhaseAnalysis) {
      // Split by H3 headers for PHASE sections
      const parts = workingContent.split(/(?=^### PHASE \d+)/m);
      
      // The first part contains the H2 title, remove it
      if (parts.length > 0 && parts[0].startsWith('## ')) {
        parts[0] = parts[0].replace(/^## .*?(\n|$)/, '').trim();
        // If the first part is now empty, remove it
        if (!parts[0]) {
          parts.shift();
        }
      }
      
      // Process each PHASE section
      const phaseSections = parts.map(part => {
        const phaseTitleMatch = part.match(/^### (PHASE \d+.*?)(?:\n|$)/);
        if (!phaseTitleMatch) return null;
        
        const sectionTitle = phaseTitleMatch[1];
        const sectionContent = part.replace(/^### .*?(\n|$)/, '').trim();
        
        return {
          title: sectionTitle,
          content: sectionContent
        };
      }).filter(section => section !== null && section.content.trim() !== '');
      
      return phaseSections;
    }
    
    // If there are no H2 headers but there are H3 headers, use H3 as section dividers
    if (!hasH2Headers && hasH3Headers) {
      
      // Extract the H1 title if it exists
      const titleMatch = workingContent.match(/^# (.*?)(?:\n|$)/);
      const mainTitle = titleMatch ? titleMatch[1] : 'Analysis';
      
      // Remove the main title from content
      let contentWithoutTitle = titleMatch ? workingContent.replace(/^# .*?(\n|$)/, '').trim() : workingContent;

      // Split by H3 headers
      const parts = contentWithoutTitle.split(/(?=^### )/m);
      
      // Process each H3 section
      const h3Sections = parts.map(part => {
        const h3TitleMatch = part.match(/^### (.*?)(?:\n|$)/);
        if (!h3TitleMatch) return null;
        
        const sectionTitle = cleanHeaderTitle(h3TitleMatch[1]);
        // Don't extract confidence, keep content as-is
        const sectionContent = part.replace(/^### .*?(\n|$)/, '').trim();
        
        return {
          title: sectionTitle,
          content: sectionContent
        };
      }).filter(section => section !== null && section.content.trim() !== '');
      
      return h3Sections;
    }
    
    // If there are no H2 or H3 headers, create a single section with the entire content
    if (!hasH2Headers && !hasH3Headers) {
      // Extract the H1 title if it exists
      const titleMatch = workingContent.match(/^# (.*?)(?:\n|$)/);
      const title = titleMatch ? titleMatch[1] : 'Analysis';
      const sectionContent = titleMatch ? workingContent.replace(/^# .*?(\n|$)/, '').trim() : workingContent;
      
      return [{
        title: title,
        content: sectionContent
      }];
    }
    
    // For Lebanon conflict analysis, we need to handle a special case where there's a main H2 header
    // followed by multiple H3 headers that should be treated as separate sections
    
    // Check if this is the Lebanon conflict analysis pattern (one H2 followed by multiple H3s)
    const isLebanonPattern = workingContent.startsWith('## Conflict Analysis in Lebanon') && hasH3Headers;
    
    if (isLebanonPattern) {
      
      // Split by H3 headers
      const parts = workingContent.split(/(?=^### )/m);
      
      // The first part might contain the H2 header, remove it if present
      if (parts.length > 0 && parts[0].startsWith('## ')) {
        parts[0] = parts[0].replace(/^## .*?(\n|$)/, '').trim();
        // If the first part is now empty, remove it
        if (!parts[0]) {
          parts.shift();
        }
      }
      
      // Process each H3 section
      const h3Sections = parts.map(part => {
        const h3TitleMatch = part.match(/^### (.*?)(?:\n|$)/);
        if (!h3TitleMatch) return null;
        
        const sectionTitle = cleanHeaderTitle(h3TitleMatch[1]);
        const sectionContent = part.replace(/^### .*?(\n|$)/, '').trim();
        
        return {
          title: sectionTitle,
          content: sectionContent
        };
      }).filter(section => section !== null && section.content.trim() !== '');
      
      return h3Sections;
    }
    
    // Otherwise, split by H2 headers as usual
    const contentParts = workingContent.split(/(?=^## )/m);
    
    // The first part might be the H1 title and intro text, or it might be empty
    const [intro, ...restSections] = contentParts;
    
    // Process the intro section if it's not empty
    let processedSections = [];
    if (intro && intro.trim()) {
      // Check if the intro has an H1 title
      const introTitleMatch = intro.match(/^# (.*?)(?:\n|$)/);
      if (introTitleMatch) {
        // Use the H1 title for the first section
        const introTitle = introTitleMatch[1];
        const introContent = intro.replace(/^# .*?(\n|$)/, '').trim();
        if (introContent) {
          // Check if the content already has section headers
          // If it does, we don't need to add an Overview title
          const hasSubheaders = /^#{2,6} /m.test(introContent);
          if (hasSubheaders) {
            // Skip adding this as a separate section, it will be handled by other sections
            console.log('Skipping Overview section because content has subheaders');
          } else {
            processedSections.push({
              title: introTitle,
              content: introContent
          });
          }
        }
      } else {
        // If no H1 title, just use the intro as a section
        // Check if the content already has section headers
        const hasSubheaders = /^#{2,6} /m.test(intro.trim());
        if (hasSubheaders) {
          // Skip adding this as a separate section, it will be handled by other sections
          console.log('Skipping Overview section because content has subheaders');
        } else {
          processedSections.push({
            title: 'Overview',
            content: intro.trim()
          });
        }
      }
    }
    
    // Process the rest of the sections
    const sections = restSections.map(sectionMarkdown => {
      const titleMatch = sectionMarkdown.match(/^## (.*?)(?:\n|$)/);
      if (!titleMatch) return null;

      const cleanedTitle = cleanHeaderTitle(titleMatch[1]);
      const sectionBodyMarkdown = sectionMarkdown.replace(/^## .*?(\n|$)/, '').trim();
      
      // Skip bold-to-header conversion: render the markdown as-is
      return {
        title: cleanedTitle,
        content: sectionBodyMarkdown
      };
    }).filter(section => section !== null);
    
    // Combine the processed intro and the rest of the sections
    processedSections = processedSections.concat(sections);
    
    // Special blockquote logic for Executive Summary (if not already handled by general first section blockquoting)
    // New Blockquote Logic: If the first section's content is "flat" (no H3+ headers), make it a blockquote.
    // This is the generic one, skip if Executive Summary was already handled.
    if (!isExecutiveSummaryBlockquotedAndHrRemoved && processedSections.length > 0) {
      const firstSection = processedSections[0];
      // Ensure content exists - remove confidence check since we're not extracting it
      if (firstSection.content && firstSection.content.trim() !== '') {
        const isFlatContent = !/^#{3,6} /m.test(firstSection.content.trim());
        const isSingleParagraph = !firstSection.content.trim().includes('\n\n');

        if (isFlatContent && isSingleParagraph) {
          firstSection.content = '> ' + firstSection.content.trim().split('\n').join('\n> ');
        }
      }
    }
    
    // Filter out empty sections
    let finalSections = processedSections.filter(section => 
      section !== null && 
      section.title.trim() !== ''
    );

    // Add problem tree section if available
    if (data?.problem_tree_content) {
      finalSections.push({
        title: 'Problem Tree Analysis',
        content: data.problem_tree_content,
        isProblemTree: true, // Flag to indicate this should use visualization
        icon: <AccountTreeIcon sx={{ fontSize: '1.2em', mr: 1, color: 'primary.main' }} />
      });
    }

    return finalSections;
  }, [extractContent, data?.problem_tree_content]);

  // Update the formatted date initially and set up the timer
  useEffect(() => {
    // Use data.completed_at instead of created_at
    const dateField = data?.completed_at || data?.updated_at || data?.created_at;
    dateFieldRef.current = dateField;
    
    if (!dateField) {
      setFormattedLastUpdated(null);
      return;
    }
    
    // Function to update the formatted date
    const updateFormattedDate = () => {
      try {
        const updatedDate = new Date(dateFieldRef.current);
        // Remove 'about' from the formatted time
        const formatted = formatDistanceToNow(updatedDate, { addSuffix: true });
        setFormattedLastUpdated(formatted.replace('about ', ''));
      } catch (e) {
        console.error('Error formatting date:', e);
        setFormattedLastUpdated(null);
      }
    };
    
    // Initial update
    updateFormattedDate();
    
    // Set up interval to update every minute (60000ms)
    const intervalId = setInterval(updateFormattedDate, 60000);
    
    // Clean up interval on unmount or when data changes
    return () => clearInterval(intervalId);
  }, [data?.completed_at, data?.updated_at, data?.created_at]);
  
  // Fetch analysis history when pillar/view data is available
  useEffect(() => {
    const fetchHistory = async () => {
      if (!data?.pillar_id || !data?.view_id) return;
      
      setLoadingHistory(true);
      try {
        const response = await fetch(`/api/analysis/pillar-history/${data.pillar_id}/${data.view_id}`);
        const result = await response.json();
        
        if (result.success && result.data) {
          setAnalysisHistory(result.data);
          // Find current analysis in history
          const currentIndex = result.data.findIndex(item => item.id === data.id);
          setCurrentHistoryIndex(currentIndex >= 0 ? currentIndex : 0);
        }
      } catch (error) {
        console.error('Error fetching analysis history:', error);
      } finally {
        setLoadingHistory(false);
      }
    };
    
    fetchHistory();
  }, [data?.pillar_id, data?.view_id, data?.id]);
  
  // Navigation handlers
  const handlePreviousAnalysis = useCallback(() => {
    if (currentHistoryIndex < analysisHistory.length - 1) {
      const nextIndex = currentHistoryIndex + 1;
      const nextAnalysis = analysisHistory[nextIndex];
      if (onNavigateToAnalysis) {
        onNavigateToAnalysis(nextAnalysis.id);
      }
    }
  }, [currentHistoryIndex, analysisHistory, onNavigateToAnalysis]);
  
  const handleNextAnalysis = useCallback(() => {
    if (currentHistoryIndex > 0) {
      const prevIndex = currentHistoryIndex - 1;
      const prevAnalysis = analysisHistory[prevIndex];
      if (onNavigateToAnalysis) {
        onNavigateToAnalysis(prevAnalysis.id);
      }
    }
  }, [currentHistoryIndex, analysisHistory, onNavigateToAnalysis]);
  
  const handleHistoryMenuOpen = useCallback((event) => {
    setHistoryMenuAnchor(event.currentTarget);
  }, []);
  
  const handleHistoryMenuClose = useCallback(() => {
    setHistoryMenuAnchor(null);
  }, []);
  
  const handleSelectHistoryItem = useCallback((analysisId) => {
    if (onNavigateToAnalysis && analysisId) {
      onNavigateToAnalysis(analysisId);
    }
    handleHistoryMenuClose();
  }, [onNavigateToAnalysis, handleHistoryMenuClose]);

  const handleViewAnalysisInAdmin = useCallback((e, analysisId) => {
    e.stopPropagation();
    const adminUrl = import.meta.env.PROD ? '/admin/' : 'http://localhost:3000/admin/';
    window.open(`${adminUrl}analysis/${analysisId}`, '_blank');
  }, []);

  const handleDeleteAnalysisClick = useCallback((e, analysisId) => {
    e.stopPropagation();
    setAnalysisToDelete(analysisId);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteAnalysisConfirm = useCallback(async () => {
    if (!analysisToDelete) return;
    
    // Check if we are deleting the currently viewed analysis
    const isDeletingCurrent = analysisToDelete === data?.id;

    try {
      await fetch(`/api/analysis/${analysisToDelete}`, { method: 'DELETE', credentials: 'include' });
      setDeleteDialogOpen(false);
      setAnalysisToDelete(null);
      handleHistoryMenuClose();
      // Refresh history list
      const response = await fetch(`/api/analysis/pillar-history/${data?.pillar_id}/${data?.view_id}`);
      const result = await response.json();
      if (result.success && result.data) {
        setAnalysisHistory(result.data);
        
        // If we deleted the current analysis, switch to the most recent one (first in the list)
        if (isDeletingCurrent && result.data.length > 0 && onNavigateToAnalysis) {
          onNavigateToAnalysis(result.data[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to delete analysis:', err);
      alert('Failed to delete analysis record');
    }
  }, [analysisToDelete, handleHistoryMenuClose, data?.pillar_id, data?.view_id, data?.id, onNavigateToAnalysis]);

  const handleDeleteAnalysisCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setAnalysisToDelete(null);
  }, []);

  const handleRegenerateAnalysis = useCallback((e) => {
    e.stopPropagation();
    setRegenerateDialogOpen(true);
  }, []);

  const handleRegenerateConfirm = useCallback(async () => {
    setRegenerateDialogOpen(false);
    
    try {
      const viewId = data?.view_id;
      const pillarId = data?.pillar_id;
      if (!viewId || !pillarId) {
        throw new Error('Missing view or pillar context for regeneration');
      }

      const saveResponse = await fetch('/api/saved-batch-configs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          name: `Temp analysis regenerate (view ${viewId}, pillar ${pillarId})`,
          views: [viewId],
          pillars: [pillarId],
          types: ['analysis'],
          scheduleEnabled: false,
          scheduleIntervalValue: 1,
          scheduleIntervalUnit: 'days',
          schedule: null,
          starred: false,
          temporary: true
        })
      });

      const saveResult = await saveResponse.json();
      if (!saveResponse.ok || !saveResult?.success) {
        throw new Error(saveResult?.error || 'Failed to create temporary batch configuration');
      }

      const configId = saveResult?.data?.id;
      if (!configId) {
        throw new Error('Temporary batch configuration created without an ID');
      }

      const runResponse = await fetch(`/api/scheduler/run-now/${configId}` , {
        method: 'POST',
        credentials: 'include'
      });

      const runResult = await runResponse.json();
      if (!runResponse.ok || !runResult?.success) {
        throw new Error(runResult?.error || 'Failed to start scheduled regeneration');
      }

      handleHistoryMenuClose();
    } catch (err) {
      console.error('Failed to regenerate analysis:', err);
      alert('Failed to start analysis regeneration: ' + err.message);
    }
  }, [data?.view_id, data?.pillar_id, handleHistoryMenuClose]);

  const handleRegenerateCancel = useCallback(() => {
    setRegenerateDialogOpen(false);
  }, []);

  const handleEditPillar = useCallback((e) => {
    e.stopPropagation();
    const adminUrl = import.meta.env.PROD ? '/admin/' : 'http://localhost:3000/admin/';
    // Use sub-pillar ID if available, otherwise use parent pillar ID
    const pillarIdToEdit = subPillarId || data?.pillar_id;
    window.open(`${adminUrl}pillars?pillarId=${pillarIdToEdit}`, '_blank');
  }, [subPillarId, data?.pillar_id]);

  const scrollToTop = useCallback(() => {
    if (folderContentRef.current) {
      folderContentRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    if (!sections || sections.length === 0) return;

    if (!sectionRefs.current || sectionRefs.current.length !== sections.length) {
      sectionRefs.current = Array(sections.length).fill(null);
    }

    setActiveTab(0);
    scrollToTop();
  }, [sections, scrollToTop]);

  // Function to scroll to a specific section
  const scrollToSection = useCallback((index) => {
    console.log('scrollToSection called with index:', index);
    console.log('sectionRefs.current[index]:', sectionRefs.current[index]);
    console.log('folderContentRef.current:', folderContentRef.current);

    if (sectionRefs.current[index] && folderContentRef.current) {
      const sectionElement = sectionRefs.current[index];
      console.log('sectionElement:', sectionElement);

      // Dynamic offset calculation:
      // - First section (index 0): needs smaller offset as it's at the top
      // - Subsequent sections: need larger offset due to accumulated section spacing
      const offset = index === 0 ? 40 : -10;
      const sectionPosition = sectionElement.offsetTop - offset;
      console.log('Calculated sectionPosition:', sectionPosition, 'with offset:', offset);

      // Set the programmatic scroll flag
      programmaticScrollRef.current = true;
      
      // Reset the flag after the scroll animation completes
      setTimeout(() => {
        programmaticScrollRef.current = false;
      }, 1000); // 1000ms should be enough for the scroll animation

      folderContentRef.current.scrollTo({
        top: sectionPosition,
        behavior: 'smooth'
      });

      setActiveTab(index);
    } else {
      console.warn('Missing refs:', {
        sectionRef: sectionRefs.current[index],
        folderContentRef: folderContentRef.current
      });
    }
  }, [setActiveTab]);

  const handleTabClick = useCallback((index) => {
    console.log('Tab clicked:', index);
    scrollToSection(index);
  }, [scrollToSection]);

  // Log when data changes for debugging
  useEffect(() => {
    if (data) {
      // If data is just an ID, log a warning
      if (typeof data === 'number' || (typeof data === 'object' && !data.content && data.id)) {
        console.warn('WARNING: Received data appears to be just an ID or object without content:', data);
      }
    }
  }, [data]);
  
  // Add a render counter to track component updates
  const renderCount = useRef(0);
  renderCount.current += 1;

  // New rendering logic order:
  // 1. Handle isLoading
  if (isLoading) {
    return (
      <Box 
        className="analysis-container" 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        flex={1}
        height="100%"
        minHeight="300px"
        sx={{ 
          position: 'relative',
          width: '100%'
        }}
      >
        <CircularProgress sx={{ color: 'var(--color-primary)' }} />
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading analysis...
        </Typography>
      </Box>
    );
  }

  // 2. Handle error
  if (error) {
    return (
      <Box 
        className="analysis-container" 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="100%"
        textAlign="center"
        padding={2}
      >
        <div>
          <ErrorIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
          <Typography color="error" variant="h6">Error loading analysis</Typography>
          <Typography color="textSecondary">
            {typeof error === 'string' ? error : (error.message || 'An unexpected error occurred. Please try again later.')}
          </Typography>
        </div>
      </Box>
    );
  }

  // 3. Handle no data / no content
  // data.content is the primary source of truth for displayable analysis.
  const contentAvailable = data && data.content;

  // Only show "No analysis" if we're confident this is truly a "no content" state
  // Avoid showing it during initial load or transitions by considering data presence
  const isDefinitelyNoContent = !isLoading && !error && !contentAvailable && data !== null && data !== undefined;

  if (isDefinitelyNoContent) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="100%" 
        width="100%" 
        position="relative"
        className="analysis-container" // Ensure consistent root class
      >
        <div className="no-analysis-message" style={{ textAlign: 'center' }}>
          <InfoOutlinedIcon sx={{ fontSize: 40, mb: 1, color: 'text.secondary' }} />
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'text.secondary',
              fontSize: 'var(--font-size-lg)',
              fontWeight: 400,
              letterSpacing: '0.01em'
          }}
          >
            No analysis available for this selection
          </Typography>
        </div>
      </Box>
    );
  }

  // 3b. Handle transition state - show loading spinner when data is null/undefined
  // This prevents "No analysis" from flashing during pillar changes
  if (!isLoading && !error && !contentAvailable && (data === null || data === undefined)) {
    return (
      <Box 
        className="analysis-container" 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        flex={1}
        height="100%"
        minHeight="300px"
        sx={{ 
          position: 'relative',
          width: '100%'
        }}
      >
        <CircularProgress sx={{ color: 'var(--color-primary)' }} />
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading analysis...
        </Typography>
      </Box>
    );
  }
  
  // 4. Render content if data is available
  // At this point, isLoading is false, error is null, and data.content is available.
  // The `sections` useMemo should have successfully processed the content.
  return (
    <Box className="analysis-container" sx={{ position: 'relative' }}>
      <PrintHeader
        viewName={viewName}
        pillarName={pillarName}
        subPillarName={subPillarName}
        analysisDate={data?.completed_at || data?.updated_at || data?.created_at}
      />
      {/* The analysis-container-inner div might be redundant if not specifically styled for a purpose beyond containing FolderTabs and ContentComponent */}
      {/* <div className="analysis-container-inner"></div> */}
      
      {sections && sections.length > 0 && ( // Ensure sections were actually created
        <>
          {!isMobile && (
            <FolderTabs
              sections={sections}
              onTabClick={handleTabClick}
            />
          )}

          <ScrollTracker
            sections={sections}
            folderContentRef={folderContentRef}
            sectionRefs={sectionRefs}
            programmaticScrollRef={programmaticScrollRef}
          />

          <ContentComponent
            sections={sections}
            folderContentRef={folderContentRef}
            sectionRefs={sectionRefs}
            handlePrint={handlePrint}
            formattedLastUpdated={formattedLastUpdated}
            viewId={data?.view_id}
            pillarId={data?.pillar_id}
            dateFieldRef={dateFieldRef}
            // Navigation props
            handlePreviousAnalysis={handlePreviousAnalysis}
            handleNextAnalysis={handleNextAnalysis}
            currentHistoryIndex={currentHistoryIndex}
            analysisHistory={analysisHistory}
            onNavigateToAnalysis={onNavigateToAnalysis}
            handleHistoryMenuOpen={handleHistoryMenuOpen}
            historyMenuAnchor={historyMenuAnchor}
            handleHistoryMenuClose={handleHistoryMenuClose}
            handleSelectHistoryItem={handleSelectHistoryItem}
            handleViewAnalysisInAdmin={handleViewAnalysisInAdmin}
            handleDeleteAnalysis={handleDeleteAnalysisClick}
            handleRegenerateAnalysis={handleRegenerateAnalysis}
            handleEditPillar={handleEditPillar}
            isSuperAdmin={isSuperAdmin}
            data={data}
          />
        </>
      )}
      {/* Fallback if sections somehow didn't render, though previous checks should prevent this */}
      {(!sections || sections.length === 0) && contentAvailable && (
         <Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
           <Typography color="textSecondary">Analysis content is available but could not be displayed.</Typography>
         </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteAnalysisCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Analysis Record?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this analysis record? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteAnalysisCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteAnalysisConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate Confirmation Dialog */}
      <Dialog
        open={regenerateDialogOpen}
        onClose={handleRegenerateCancel}
        aria-labelledby="regenerate-dialog-title"
        aria-describedby="regenerate-dialog-description"
      >
        <DialogTitle id="regenerate-dialog-title">
          Regenerate Analysis?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="regenerate-dialog-description">
            This will start a background process to generate a new analysis for this view and pillar. The process may take a minute to complete.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRegenerateCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRegenerateConfirm} color="primary" variant="contained" autoFocus>
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

// Wrap the main component with the TabProvider
const AnalysisComponentWithProvider = memo((props) => {
  return (
    <TabProvider>
      <AnalysisComponentImpl {...props} data={props.data} />
    </TabProvider>
  );
});

// Wrapper component that will properly track renders
const AnalysisComponentWrapper = (props) => {
  // This wrapper will help detect unnecessary renders
  // Track previous props for manual comparison
  const prevPropsRef = useRef(null);
  const renderCountRef = useRef(0);
  
  // Increment render count
  renderCountRef.current += 1;
  
  // Add a timestamp to track render frequency
  const renderTimestamp = useRef(Date.now());
  const now = Date.now();
  const timeSinceLastRender = now - renderTimestamp.current;
  renderTimestamp.current = now;
  
  // Use memo to avoid unnecessary re-renders within the same wrapper render cycle
  // This helps when the parent renders and passes the same data reference
  const memoizedComponent = useMemo(() => {
    return <AnalysisComponentWithProvider {...props} />;
  }, [
    props.data?.id,
    Boolean(props.isLoading),
    Boolean(props.error),
    props.data?.content ? JSON.stringify(props.data.content).substring(0, 20) : null
  ]);
  
  useEffect(() => {
    const prevProps = prevPropsRef.current;
    
    if (prevProps && process.env.NODE_ENV === 'development') {
      // Check if props actually changed
      const dataIdSame = props.data?.id === prevProps.data?.id;
      const isLoadingSame = props.isLoading === prevProps.isLoading;
      const errorSame = props.error === prevProps.error;
      const propsIdentical = dataIdSame && isLoadingSame && errorSame;
      
      if (propsIdentical) {
        console.warn('⚠️ AnalysisComponent re-rendered with same props');
      }
    }
    
    // Store current props for next render
    prevPropsRef.current = {...props};
  });
  
  // Return the memoized implementation
  return memoizedComponent;
};

// Apply a proper React.memo with custom comparison function to prevent unnecessary re-renders
const arePropsEqual = (prevProps, nextProps) => {
  // Only re-render when meaningful props change
  const sameDataId = prevProps.data?.id === nextProps.data?.id;
  const sameIsLoading = prevProps.isLoading === nextProps.isLoading; 
  const sameError = prevProps.error === nextProps.error;
  
  // For deep comparison of content, do a first-level string comparison of content 
  const sameContent = prevProps.data?.content && nextProps.data?.content
    ? JSON.stringify(prevProps.data.content).substring(0, 50) === 
      JSON.stringify(nextProps.data.content).substring(0, 50)
    : prevProps.data?.content === nextProps.data?.content;
  
  // Check if data exists in one but not the other
  const bothHaveData = Boolean(prevProps.data) === Boolean(nextProps.data);
  
  // Check if both have the same effective timestamp (completed_at > updated_at > created_at)
  const prevEffectiveAt = prevProps.data?.completed_at || prevProps.data?.updated_at || prevProps.data?.created_at;
  const nextEffectiveAt = nextProps.data?.completed_at || nextProps.data?.updated_at || nextProps.data?.created_at;
  const sameEffectiveAt = prevEffectiveAt === nextEffectiveAt;
  
  // Return true when props are equal (component should NOT re-render)
  const areEqual = sameDataId && sameIsLoading && sameError && bothHaveData && 
                   (sameDataId ? sameContent && sameEffectiveAt : true);
  
  // Log the comparison result if it would prevent a re-render
  return areEqual;
};

// Create the final memoized component
const AnalysisComponent = memo(AnalysisComponentWrapper, arePropsEqual);

// Set display name for debugging
AnalysisComponent.displayName = 'AnalysisComponent';

export default AnalysisComponent;

export { FolderTabs };
